gsap.registerPlugin(ScrollTrigger);

// A late full refresh can temporarily remove every pin spacer. If the shopper
// has already scrolled past the long Restful scene, that shortens the document
// by several viewports and lets the browser clamp the current scroll position.
// Keep automatic refreshes to real viewport changes; the guarded load refresh
// below still runs while the page is near the top.
ScrollTrigger.config({
	autoRefreshEvents: "visibilitychange,resize",
	ignoreMobileResize: true,
});

// When this deferred bundle arrives after the user has started scrolling,
// creating pin spacers moves the visible downstream section. Record a stable
// section anchor before any ScrollTrigger is created and restore it after all
// synchronous pin setup has finished.
const na6cInitialScrollAnchor = (() => {
	if (window.scrollY <= 1) return null;

	const probe = document.elementFromPoint(
		window.innerWidth / 2,
		Math.min(window.innerHeight / 2, window.innerHeight - 1),
	);
	const element = probe?.closest("section") || probe;
	if (!element) return null;

	return {
		element,
		top: element.getBoundingClientRect().top,
	};
})();

// Design revision — the particle texture is disabled. Blur builds while the
// copy remains readable, then opacity carries it through the rest of the exit.
const createNa6cTextExitEffect = (scope) => {
	const blur = scope?.querySelector("[data-na6c-exit-blur]");
	const reset = () => {
		blur?.setAttribute("stdDeviation", "0");
	};
	const addExit = (
		timeline,
		target,
		{ duration = 0.72, position = timeline.duration() } = {},
	) => {
		const blurDuration = duration * 0.35;
		const fadeDuration = duration - blurDuration;

		if (blur) {
			timeline.to(
				blur,
				{
					attr: { stdDeviation: 4 },
					duration: blurDuration,
					ease: "power2.inOut",
				},
				position,
			);
		}
		timeline
			.to(
				target,
				{
					autoAlpha: 0.78,
					duration: blurDuration,
					ease: "power1.in",
				},
				position,
			)
			.to(
				target,
				{
					autoAlpha: 0,
					duration: fadeDuration,
					ease: "power2.out",
				},
				position + blurDuration,
			);
		return { duration, position };
	};

	reset();
	return { addExit, reset };
};

const connectivityCard = document.querySelector("[data-na6c-connectivity]");
const connectivityDraw = connectivityCard?.querySelector(
	"[data-na6c-connectivity-draw]",
);

if (connectivityCard && connectivityDraw) {
	const reduceMotion = window.matchMedia(
		"(prefers-reduced-motion: reduce)",
	).matches;

	if (reduceMotion) {
		connectivityDraw.classList.add("is-drawn");
	} else {
		ScrollTrigger.create({
			trigger: connectivityCard,
			start: "top 66.667%",
			once: true,
			onEnter: () => connectivityDraw.classList.add("is-drawn"),
		});
	}
}

const restfulSection = document.querySelector("[data-na6c-restful]");

if (restfulSection) {
	const restfulStage = restfulSection.querySelector(
		"[data-na6c-restful-stage]",
	);
	const restfulDeviceStage = restfulSection.querySelector(
		"[data-na6c-restful-device-stage]",
	);
	const restfulDevice = restfulSection.querySelector(
		"[data-na6c-restful-device]",
	);
	const restfulTextMask = restfulSection.querySelector(
		"[data-na6c-restful-text-mask]",
	);
	const restfulShade = restfulSection.querySelector(
		"[data-na6c-restful-shade]",
	);
	const restfulFocusCopy = restfulSection.querySelector(
		"[data-na6c-restful-focus-copy]",
	);
	const restfulFocusExitEffect = createNa6cTextExitEffect(restfulSection);
	const restfulFinalTitle = restfulSection.querySelector(
		"[data-na6c-restful-final-title]",
	);
	const restfulFeatures = restfulSection.querySelectorAll(
		"[data-na6c-restful-feature]",
	);
	const restfulProductHeader = document.querySelector(
		".product-specs__header",
	);
	const restfulHeader = document.querySelector(".wrapper-header");
	const restfulDesign = {
		clipCoverPadding: 1.55,
		clipOriginClearance: 2.2,
		// TUNING — logical source-image size. The current PNG is a 2000 × 1625
		// export of this same 1280 × 1040 coordinate system.
		deviceSource: { width: 1280, height: 1040 },
		// TUNING — measured screen rectangle inside the source image. The intro
		// uses this box (not the outer device) to cover every viewport.
		deviceScreen: { x: 274, y: 70, width: 728, height: 544 },
		height: 1024,
		lines: {
			"deep-focus": { x: 658.26, y: 503 },
			"restful-colors": { x: 601.716, y: 363 },
		},
		lineScale: 2.4,
		// Source SVG viewBoxes. Firefox reports a 0 × 0 getBBox() for paths
		// inside an unpainted mask, so these keep the layout deterministic.
		pathBounds: {
			"deep-focus": {
				height: 51.69999694824219,
				width: 287.95001220703125,
				x: 0,
				y: 13.899999618530273,
			},
			"restful-colors": {
				height: 45.55000686645508,
				width: 342.1499938964844,
				x: 0,
				y: 13.899999618530273,
			},
		},
		// TUNING — expansion origin: lower curved stroke of the “c” in “colors”.
		// Source-path point (194.6, 50.5), converted into desktop coordinates.
		originX: 601.716 + 201 * 2.4,
		originY: 363 + 48.42 * 2.4,
		width: 1920,
	};
	// TUNING — Figma final-state anchors. targetImageHeightRatio preserves the
	// reference size; maxWidthRatio lets tall screens grow without clipping.
	const restfulFinalDeviceDesigns = {
		desktop: {
			centerXRatio: 0.5,
			centerYRatio: (156 + 682.5 / 2) / 1024,
			maxWidthRatio: 0.52,
			targetImageHeightRatio: 682.5 / 1024,
		},
		tablet: {
			centerXRatio: (62.905181884765625 + 894.1896362304688 / 2) / 1024,
			centerYRatio: 484 / 1260,
			maxWidthRatio: 0.94,
			targetImageHeightRatio: (894.1896362304688 * 1040) / 1280 / 1260,
		},
		mobile: {
			centerXRatio: (-4.71453857421875 + 397.09930419921875 / 2) / 375,
			centerYRatio: (57.13066101074219 + 413.9755859375 / 2) / 732,
			maxWidthRatio: 1.16,
			targetImageHeightRatio: (397.09930419921875 * 1040) / 1280 / 732,
		},
	};
	const restfulResponsiveDesigns = {
		mobile: {
			finalTitleFromY: -44,
			height: 732,
			lineHeight: 655,
			// TUNING — 316 px Figma text width / 342.15 px path width.
			lineScale: 316 / 342.15,
			lines: {
				"deep-focus": { top: 319 },
				"restful-colors": { top: 272.4501953125 },
			},
			width: 375,
		},
		tablet: {
			finalTitleFromY: 44,
			height: 1260,
			lineHeight: 1260,
			// TUNING — tablet intro text width. The target frame is 580 px
			// wide; divide by the source path width to get the SVG scale.
			lineScale: 580 / 342.15,
			lines: {
				// TUNING — tablet intro line tops in the 1024 × 1260 design.
				// Keep the 90 px gap while centering the two-line lockup.
				"deep-focus": { top: 620 },
				"restful-colors": { top: 530 },
			},
			width: 1024,
		},
	};
	const restfulResponsiveOrigin = {
		x:
			(restfulDesign.originX - restfulDesign.lines["restful-colors"].x) /
			restfulDesign.lineScale,
		y:
			(restfulDesign.originY - restfulDesign.lines["restful-colors"].y) /
			restfulDesign.lineScale,
	};
	const restfulClipState = { scale: 1 };
	const restfulDeviceState = { progress: 0, revealProgress: 0 };
	const restfulGeometry = {
		centerX: restfulDesign.originX,
		centerY: restfulDesign.originY,
		height: restfulDesign.height,
		layoutScale: 1,
		offsetX: 0,
		offsetY: 0,
		responsiveDesign: null,
		width: restfulDesign.width,
	};
	// Cached during ScrollTrigger refresh. Animation frames only interpolate
	// numbers and write SVG attributes; they no longer re-read layout geometry.
	const restfulDeviceBoxes = { intro: null, focus: null, final: null };
	const restfulPathBounds = Object.fromEntries(
		Object.entries(restfulDesign.pathBounds).map(([name, bounds]) => [
			name,
			{ ...bounds },
		]),
	);
	const getRestfulDesktopLineX = (name) => {
		const bounds = restfulPathBounds[name];

		if (!bounds) return restfulDesign.lines[name]?.x ?? 0;

		return (
			(restfulDesign.width - bounds.width * restfulDesign.lineScale) / 2 -
			bounds.x * restfulDesign.lineScale
		);
	};
	let restfulHeaderTween = null;

	// TUNING — mobile product-header transition while this pinned scene is
	// active. ScrollTrigger calls the same helper in both scroll directions.
	const setRestfulHeaderHidden = (hidden, { immediate = false } = {}) => {
		if (!restfulProductHeader || !restfulHeader) return;

		const shouldHide =
			hidden && window.matchMedia("(max-width: 750px)").matches;
		restfulHeaderTween?.kill();

		if (immediate) {
			gsap.set([restfulProductHeader, restfulHeader], {
				autoAlpha: shouldHide ? 0 : 1,
			});
			return;
		}

		restfulHeaderTween = gsap.to([restfulProductHeader, restfulHeader], {
			autoAlpha: shouldHide ? 0 : 1,
			duration: 0.4,
			ease: "power2.out",
			overwrite: "auto",
		});
	};
	const clearRestfulHeaderState = () => {
		restfulHeaderTween?.kill();
		restfulHeaderTween = null;
		if (!restfulProductHeader || !restfulHeader) return;
		gsap.set([restfulProductHeader, restfulHeader], {
			clearProps: "opacity,visibility",
		});
	};

	// Paths now ship in the initial SVG DOM. Avoiding a runtime `d` mutation
	// fixes inconsistent counter rendering inside letters such as “e” and “p”.
	const restfulPathsReady = ["restful-colors", "deep-focus"].every((name) =>
		restfulSection
			.querySelector(`[data-na6c-restful-clip-line="${name}"]`)
			?.getAttribute("d"),
	);

	const getRestfulStageSize = () => ({
		height: Math.max(restfulStage?.clientHeight || 0, 1),
		width: Math.max(restfulStage?.clientWidth || 0, 1),
	});
	const getRestfulResponsiveDesign = () => {
		const width = window.innerWidth;

		if (width <= 750) return restfulResponsiveDesigns.mobile;
		if (width <= 1024) return restfulResponsiveDesigns.tablet;
		return null;
	};

	const getRestfulScreenCoverDeviceBox = (zoom = 1) => {
		const { height, width } = getRestfulStageSize();
		const source = restfulDesign.deviceSource;
		const screen = restfulDesign.deviceScreen;
		// `max` is intentional: it makes the screen rectangle cover both axes,
		// so narrow/portrait viewports crop inside the screen instead of showing
		// the surrounding bezel above or below it.
		const scale =
			Math.max(width / screen.width, height / screen.height) * zoom;
		const screenCenterX = screen.x + screen.width / 2;
		const screenCenterY = screen.y + screen.height / 2;

		return {
			height: source.height * scale,
			width: source.width * scale,
			x: width / 2 - screenCenterX * scale,
			y: height / 2 - screenCenterY * scale,
		};
	};

	const getRestfulLargeDeviceBox = () => getRestfulScreenCoverDeviceBox();

	// A very small push-in prevents a visible edge when sub-pixel rounding is
	// different between SVG and the pinned stage.
	const getRestfulFocusDeviceBox = () =>
		getRestfulScreenCoverDeviceBox(1.025);

	const getRestfulFinalDeviceBox = () => {
		const { height, width } = getRestfulStageSize();
		const source = restfulDesign.deviceSource;
		const responsiveDesign = getRestfulResponsiveDesign();
		const design = responsiveDesign
			? width <= 750
				? restfulFinalDeviceDesigns.mobile
				: restfulFinalDeviceDesigns.tablet
			: restfulFinalDeviceDesigns.desktop;
		const sourceAspect = source.width / source.height;
		// Width reacts to both viewport axes: short screens shrink to avoid
		// clipping; tall screens can grow until the per-device width cap.
		const deviceWidth = Math.min(
			width * design.maxWidthRatio,
			height * design.targetImageHeightRatio * sourceAspect,
		);
		const deviceHeight = deviceWidth / sourceAspect;
		// TUNING — proportional device center from the three Figma frames. These
		// slight non-50% values preserve the optical alignment on tablet/mobile.
		const centerX = width * design.centerXRatio;
		const centerY = height * design.centerYRatio;

		return {
			height: deviceHeight,
			width: deviceWidth,
			x: centerX - deviceWidth / 2,
			y: centerY - deviceHeight / 2,
		};
	};

	const setRestfulDeviceBox = (box) => {
		Object.entries(box).forEach(([name, value]) => {
			restfulDevice?.setAttribute(name, value.toFixed(3));
		});
	};

	const removeRestfulDeviceMask = () => {
		restfulDevice?.removeAttribute("mask");
		// Clean up the previous implementation during theme hot reloads.
		restfulDevice?.removeAttribute("clip-path");
	};

	const renderRestfulDevice = () => {
		const intro = restfulDeviceBoxes.intro || getRestfulLargeDeviceBox();
		const focus = restfulDeviceBoxes.focus || getRestfulFocusDeviceBox();
		const final = restfulDeviceBoxes.final || getRestfulFinalDeviceBox();
		const progress = restfulDeviceState.progress;
		const revealProgress = restfulDeviceState.revealProgress;
		const large = {
			height: gsap.utils.interpolate(
				intro.height,
				focus.height,
				revealProgress,
			),
			width: gsap.utils.interpolate(
				intro.width,
				focus.width,
				revealProgress,
			),
			x: gsap.utils.interpolate(intro.x, focus.x, revealProgress),
			y: gsap.utils.interpolate(intro.y, focus.y, revealProgress),
		};

		setRestfulDeviceBox({
			height: gsap.utils.interpolate(
				large.height,
				final.height,
				progress,
			),
			width: gsap.utils.interpolate(large.width, final.width, progress),
			x: gsap.utils.interpolate(large.x, final.x, progress),
			y: gsap.utils.interpolate(large.y, final.y, progress),
		});
	};

	const renderRestfulClip = () => {
		const responsiveDesign = restfulGeometry.responsiveDesign;

		if (responsiveDesign) {
			const { height, width } = restfulGeometry;
			const widthScale = width / responsiveDesign.width;
			const referenceLine = responsiveDesign.lines["restful-colors"];
			const referenceBounds = restfulPathBounds["restful-colors"];
			const referenceScale = responsiveDesign.lineScale * widthScale;
			const referenceX = referenceBounds
				? (width - referenceBounds.width * referenceScale) / 2
				: width / 2;
			const referenceTop =
				(referenceLine.top / responsiveDesign.lineHeight) * height;
			const referenceY = referenceBounds
				? referenceTop - referenceBounds.y * referenceScale
				: referenceTop;
			const centerX =
				referenceX + restfulResponsiveOrigin.x * referenceScale;
			const centerY =
				referenceY + restfulResponsiveOrigin.y * referenceScale;

			Object.entries(responsiveDesign.lines).forEach(([name, line]) => {
				const path = restfulSection.querySelector(
					`[data-na6c-restful-clip-line="${name}"]`,
				);
				const bounds = restfulPathBounds[name];

				if (!path || !bounds) return;

				const lineScale = responsiveDesign.lineScale * widthScale;
				const lineX = (width - bounds.width * lineScale) / 2;
				const lineTop =
					(line.top / responsiveDesign.lineHeight) * height;
				const lineY = lineTop - bounds.y * lineScale;
				path.setAttribute(
					"transform",
					`translate(${centerX} ${centerY}) scale(${restfulClipState.scale}) translate(${-centerX} ${-centerY}) translate(${lineX} ${lineY}) scale(${lineScale})`,
				);
			});

			return;
		}

		const { centerX, centerY, layoutScale, offsetX, offsetY } =
			restfulGeometry;
		const scale = restfulClipState.scale;

		Object.entries(restfulDesign.lines).forEach(([name, line]) => {
			const path = restfulSection.querySelector(
				`[data-na6c-restful-clip-line="${name}"]`,
			);
			const lineX = getRestfulDesktopLineX(name);

			path?.setAttribute(
				"transform",
				`translate(${centerX} ${centerY}) scale(${scale}) translate(${-centerX} ${-centerY}) translate(${offsetX} ${offsetY}) scale(${layoutScale}) translate(${lineX} ${line.y}) scale(${restfulDesign.lineScale})`,
			);
		});
	};

	const getRestfulClipCoverScale = () => {
		const { centerX, centerY, height, layoutScale, width } =
			restfulGeometry;
		const farthestCorner = Math.max(
			Math.hypot(centerX, centerY),
			Math.hypot(width - centerX, centerY),
			Math.hypot(centerX, height - centerY),
			Math.hypot(width - centerX, height - centerY),
		);
		const scaledClearance = getRestfulResponsiveDesign()
			? restfulDesign.clipOriginClearance * layoutScale
			: restfulDesign.clipOriginClearance *
				restfulDesign.lineScale *
				layoutScale;

		return (
			(farthestCorner / scaledClearance) * restfulDesign.clipCoverPadding
		);
	};

	const syncRestfulGeometry = () => {
		const { height, width } = getRestfulStageSize();
		const responsiveDesign = getRestfulResponsiveDesign();
		["restful-colors", "deep-focus"].forEach((name) => {
			const path = restfulSection.querySelector(
				`[data-na6c-restful-clip-line="${name}"]`,
			);

			if (!path) return;

			try {
				const bounds = path.getBBox();

				if (
					Number.isFinite(bounds.x) &&
					Number.isFinite(bounds.y) &&
					Number.isFinite(bounds.width) &&
					Number.isFinite(bounds.height) &&
					bounds.width > 0 &&
					bounds.height > 0
				) {
					restfulPathBounds[name] = {
						height: bounds.height,
						width: bounds.width,
						x: bounds.x,
						y: bounds.y,
					};
				}
			} catch {
				// Keep the source-viewBox fallback when the SVG engine cannot
				// measure paths inside <defs>/<mask>.
			}
		});
		const layoutScale = Math.max(
			width / restfulDesign.width,
			height / restfulDesign.height,
		);
		const offsetX = (width - restfulDesign.width * layoutScale) / 2;
		const offsetY = (height - restfulDesign.height * layoutScale) / 2;

		if (responsiveDesign) {
			const referenceLine = responsiveDesign.lines["restful-colors"];
			const referenceBounds = restfulPathBounds["restful-colors"];
			const referenceScale =
				responsiveDesign.lineScale * (width / responsiveDesign.width);
			const referenceX = referenceBounds
				? (width - referenceBounds.width * referenceScale) / 2
				: width / 2;
			const referenceTop =
				(referenceLine.top / responsiveDesign.lineHeight) * height;
			const referenceY = referenceBounds
				? referenceTop - referenceBounds.y * referenceScale
				: referenceTop;

			restfulGeometry.centerX =
				referenceX + restfulResponsiveOrigin.x * referenceScale;
			restfulGeometry.centerY =
				referenceY + restfulResponsiveOrigin.y * referenceScale;
		} else {
			const referenceLine = restfulDesign.lines["restful-colors"];
			const referenceX = getRestfulDesktopLineX("restful-colors");

			restfulGeometry.centerX =
				offsetX +
				(referenceX +
					restfulResponsiveOrigin.x * restfulDesign.lineScale) *
					layoutScale;
			restfulGeometry.centerY =
				offsetY +
				(referenceLine.y +
					restfulResponsiveOrigin.y * restfulDesign.lineScale) *
					layoutScale;
		}
		restfulGeometry.height = height;
		restfulGeometry.offsetX = offsetX;
		restfulGeometry.offsetY = offsetY;
		restfulGeometry.responsiveDesign = responsiveDesign;
		restfulGeometry.width = width;
		restfulGeometry.layoutScale = responsiveDesign
			? responsiveDesign.lineScale * (width / responsiveDesign.width)
			: layoutScale;
		gsap.set(restfulFinalTitle, {
			x: 0,
			xPercent: 0,
		});
		restfulDeviceBoxes.intro = getRestfulLargeDeviceBox();
		restfulDeviceBoxes.focus = getRestfulFocusDeviceBox();
		restfulDeviceBoxes.final = getRestfulFinalDeviceBox();

		restfulDeviceStage?.setAttribute("viewBox", `0 0 ${width} ${height}`);
		// Keep the user-space mask exactly aligned to the current pinned stage.
		Object.entries({ x: 0, y: 0, width, height }).forEach(
			([name, value]) => {
				restfulTextMask?.setAttribute(name, value);
			},
		);
		restfulDevice?.setAttribute("preserveAspectRatio", "none");
		renderRestfulClip();
	};

	const restfulMedia = gsap.matchMedia
		? gsap.matchMedia()
		: {
				add: (query, callback) => {
					const mediaQuery = window.matchMedia(query);
					let cleanup;
					const sync = () => {
						if (mediaQuery.matches && !cleanup) {
							cleanup = callback() || (() => {});
						} else if (!mediaQuery.matches && cleanup) {
							cleanup();
							cleanup = undefined;
						}
					};

					sync();
					if (mediaQuery.addEventListener) {
						mediaQuery.addEventListener("change", sync);
					} else {
						mediaQuery.addListener?.(sync);
					}
				},
			};

	restfulMedia.add("(prefers-reduced-motion: no-preference)", () => {
		syncRestfulGeometry();

		if (!restfulPathsReady || !restfulTextMask?.id || !restfulDevice) {
			restfulSection.classList.add("is-static");
			removeRestfulDeviceMask();
			setRestfulDeviceBox(restfulDeviceBoxes.final);
			gsap.set(restfulDeviceStage, { autoAlpha: 1 });
			gsap.set([restfulFinalTitle, ...restfulFeatures], {
				autoAlpha: 1,
				y: 0,
			});
			return undefined;
		}

		restfulSection.classList.remove("is-static");
		restfulSection.classList.add("is-motion-ready");
		restfulClipState.scale = 1;
		restfulDeviceState.progress = 0;
		restfulDeviceState.revealProgress = 0;
		restfulFocusExitEffect.reset();
		// A real alpha mask preserves compound-path counters in e/p; clipPath
		// unions could flatten those holes in some storefront browsers.
		restfulDevice.setAttribute("mask", `url(#${restfulTextMask.id})`);
		renderRestfulClip();
		renderRestfulDevice();
		gsap.set(restfulDeviceStage, { autoAlpha: 1 });
		gsap.set([restfulShade, restfulFocusCopy], { autoAlpha: 0 });
		gsap.set(restfulFocusCopy, { y: 44 });
		gsap.set(restfulFinalTitle, {
			autoAlpha: 0,
			y: getRestfulResponsiveDesign()?.finalTitleFromY ?? -44,
		});
		gsap.set(restfulFeatures, { autoAlpha: 0, y: 54 });

		const restfulTimeline = gsap.timeline({
			defaults: { ease: "none" },
			scrollTrigger: {
				anticipatePin: 1,
				end: () => `+=${restfulStage.clientHeight * 5}`,
				invalidateOnRefresh: true,
				pin: true,
				scrub: 0.75,
				start: "top top",
				trigger: restfulSection,
				onToggle: (self) => setRestfulHeaderHidden(self.isActive),
				onRefresh: (self) =>
					setRestfulHeaderHidden(self.isActive, { immediate: true }),
				onRefreshInit: () => {
					syncRestfulGeometry();
					renderRestfulDevice();
				},
			},
		});

		restfulTimeline
			.addLabel("intro", 0)
			.to(restfulClipState, {
				duration: 0.35,
				onUpdate: renderRestfulClip,
				scale: 1,
			})
			.to(restfulClipState, {
				duration: 1.9,
				ease: "none",
				onUpdate: renderRestfulClip,
				scale: 12,
			})
			.to(restfulClipState, {
				duration: 0.72,
				ease: "power1.in",
				onUpdate: renderRestfulClip,
				scale: getRestfulClipCoverScale,
			})
			.to(
				restfulDeviceState,
				{
					duration: 0.72,
					ease: "power1.in",
					onUpdate: renderRestfulDevice,
					revealProgress: 1,
				},
				"<",
			)
			.addLabel("full-screen")
			.to(restfulShade, { autoAlpha: 1, duration: 0.48 })
			.to(
				restfulFocusCopy,
				{
					autoAlpha: 1,
					duration: 0.72,
					ease: "power2.out",
					y: 0,
				},
				"-=0.12",
			)
			.to(restfulFocusCopy, { duration: 0.85, y: 0 });
		const restfulFocusExit = restfulFocusExitEffect.addExit(
			restfulTimeline,
			restfulFocusCopy,
		);
		restfulTimeline
			.to(
				restfulShade,
				{ autoAlpha: 0, duration: restfulFocusExit.duration },
				restfulFocusExit.position,
			)
			.addLabel("final")
			.to(restfulDeviceState, {
				duration: 1.65,
				ease: "power2.inOut",
				onUpdate: renderRestfulDevice,
				progress: 1,
			})
			.to(
				restfulFinalTitle,
				{
					autoAlpha: 1,
					duration: 0.7,
					ease: "power2.out",
					y: 0,
				},
				"-=0.8",
			)
			.to(
				restfulFeatures,
				{
					autoAlpha: 1,
					duration: 0.75,
					ease: "power2.out",
					stagger: 0.08,
					y: 0,
				},
				"-=0.58",
			)
			.to(restfulFeatures, { duration: 0.55, y: 0 });

		return () => {
			restfulTimeline.scrollTrigger?.kill();
			restfulTimeline.kill();
			clearRestfulHeaderState();
			restfulClipState.scale = 1;
			restfulDeviceState.progress = 1;
			restfulDeviceState.revealProgress = 1;
			restfulFocusExitEffect.reset();
			syncRestfulGeometry();
			renderRestfulDevice();
			removeRestfulDeviceMask();
			restfulSection.classList.remove("is-motion-ready");
			gsap.set(
				[
					restfulDeviceStage,
					restfulShade,
					restfulFocusCopy,
					restfulFinalTitle,
					...restfulFeatures,
				],
				{ clearProps: "opacity,transform,visibility" },
			);
		};
	});

	restfulMedia.add("(prefers-reduced-motion: reduce)", () => {
		clearRestfulHeaderState();
		const renderStaticRestful = () => {
			syncRestfulGeometry();
			restfulDeviceState.progress = 1;
			restfulDeviceState.revealProgress = 1;
			setRestfulDeviceBox(restfulDeviceBoxes.final);
		};

		restfulSection.classList.remove("is-motion-ready");
		restfulSection.classList.add("is-static");
		removeRestfulDeviceMask();
		renderStaticRestful();
		gsap.set([restfulShade, restfulFocusCopy], { autoAlpha: 0 });
		gsap.set([restfulDeviceStage, restfulFinalTitle, ...restfulFeatures], {
			autoAlpha: 1,
			y: 0,
		});

		const resizeObserver = window.ResizeObserver
			? new ResizeObserver(renderStaticRestful)
			: null;
		if (resizeObserver) {
			resizeObserver.observe(restfulStage);
		} else {
			window.addEventListener("resize", renderStaticRestful, {
				passive: true,
			});
		}

		return () => {
			resizeObserver?.disconnect();
			if (!resizeObserver) {
				window.removeEventListener("resize", renderStaticRestful);
			}
			restfulSection.classList.remove("is-static");
		};
	});
}

const academicSection = document.querySelector("[data-na6c-academic]");

if (academicSection) {
	const academicCopy = academicSection.querySelector(".na6c-academic__copy");
	const academicDevice = academicSection.querySelector(
		"[data-na6c-academic-device]",
	);
	const academicPanels = [
		...academicSection.querySelectorAll("[data-na6c-academic-panel]"),
	];
	const academicLeft = academicSection.querySelector(
		".na6c-academic__panel--left",
	);
	const academicRight = academicSection.querySelector(
		".na6c-academic__panel--right",
	);
	const academicDocs = academicSection.querySelector(
		".na6c-academic__app--docs",
	);
	const academicSlides = academicSection.querySelector(
		".na6c-academic__app--slides",
	);
	const academicTabs = academicSection.querySelector(
		"[data-na6c-academic-tabs]",
	);
	const academicTabItems = [
		...academicSection.querySelectorAll("[data-na6c-tab]"),
	];
	const academicTabDevices = [
		...academicSection.querySelectorAll("[data-na6c-tab-device]"),
	];
	const academicReduceMotion = window.matchMedia(
		"(prefers-reduced-motion: reduce)",
	).matches;
	const academicMobile = window.matchMedia("(max-width: 750px)").matches;
	const academicDesktop = window.matchMedia("(min-width: 1025px)").matches;
	const academicWorkflowEnabled = Boolean(
		academicCopy && academicDevice && academicTabs,
	);
	const academicMovingPanels = academicPanels;
	let activeAcademicTab = null;
	let academicDeviceTabTween = null;
	let academicTabsReady = false;
	let academicLayoutRefreshTimer = null;

	// Mobile accordion panels animate their height for 0.4s. Once that layout
	// change has settled, remeasure only the triggers below this section. A full
	// refresh would also tear down the long Restful pin above the current scroll
	// position and can make the browser clamp or jump the page.
	const scheduleAcademicLayoutRefresh = () => {
		if (!academicMobile) return;

		window.clearTimeout(academicLayoutRefreshTimer);
		academicLayoutRefreshTimer = window.setTimeout(
			() => {
				const academicPosition = Node.DOCUMENT_POSITION_FOLLOWING;
				const downstreamTriggers = ScrollTrigger.getAll().filter(
					(trigger) =>
						trigger.trigger instanceof Element &&
						Boolean(
							academicSection.compareDocumentPosition(
								trigger.trigger,
							) & academicPosition,
						),
				);

				downstreamTriggers.forEach((trigger) => trigger.refresh());
				ScrollTrigger.update();
			},
			academicReduceMotion ? 0 : 450,
		);
	};

	const getAcademicPanelOffset = (panel) => {
		const deviceRect = academicDevice.getBoundingClientRect();
		const panelRect = panel.getBoundingClientRect();

		return {
			x:
				deviceRect.left +
				deviceRect.width / 2 -
				(panelRect.left + panelRect.width / 2),
			y:
				deviceRect.top +
				deviceRect.height / 2 -
				(panelRect.top + panelRect.height / 2),
		};
	};

	const resetAcademicTabSelection = ({ restoreDevice = true } = {}) => {
		activeAcademicTab = null;
		academicTabs.classList.remove("has-expanded-tab");

		academicTabItems.forEach((item) => {
			const button = item.querySelector("[data-na6c-tab-btn]");
			const description = item.querySelector("[data-na6c-tab-desc]");
			const icon = item.querySelector(".na6c-academic__tab-icon");

			item.classList.remove("is-expanded");
			button?.setAttribute("aria-expanded", "false");
			description?.setAttribute("aria-hidden", "true");
			if (icon) icon.textContent = "+";
		});

		academicDeviceTabTween?.kill();
		academicDeviceTabTween = null;
		gsap.killTweensOf(academicTabDevices);
		academicTabDevices.forEach((device) => {
			device.classList.remove("is-active");
			gsap.set(device, { clearProps: "opacity,visibility" });
		});

		if (restoreDevice) {
			gsap.set(academicDevice, { autoAlpha: 1 });
		}
	};

	const setAcademicTab = (tabValue) => {
		if (!academicTabsReady) return;

		const nextTab = activeAcademicTab === tabValue ? null : tabValue;
		const previousDevice = academicTabDevices.find(
			(device) => device.dataset.na6cTabDevice === activeAcademicTab,
		);
		const nextDevice = academicTabDevices.find(
			(device) => device.dataset.na6cTabDevice === nextTab,
		);
		const transitionDuration = academicReduceMotion ? 0 : 0.36;

		activeAcademicTab = nextTab;
		academicTabs.classList.toggle("has-expanded-tab", Boolean(nextTab));

		academicTabItems.forEach((item) => {
			const expanded = item.dataset.na6cTab === nextTab;
			const button = item.querySelector("[data-na6c-tab-btn]");
			const description = item.querySelector("[data-na6c-tab-desc]");
			const icon = item.querySelector(".na6c-academic__tab-icon");

			item.classList.toggle("is-expanded", expanded);
			button?.setAttribute("aria-expanded", String(expanded));
			description?.setAttribute("aria-hidden", String(!expanded));
			if (icon) icon.textContent = expanded ? "−" : "+";
		});

		if (academicMobile) {
			scheduleAcademicLayoutRefresh();
			return;
		}

		academicDeviceTabTween?.kill();
		gsap.killTweensOf(academicTabDevices);
		academicTabDevices.forEach((device) => {
			if (device !== previousDevice && device !== nextDevice) {
				device.classList.remove("is-active");
				gsap.set(device, { clearProps: "opacity,visibility" });
			}
		});

		if (previousDevice && previousDevice !== nextDevice) {
			gsap.to(previousDevice, {
				autoAlpha: 0,
				duration: transitionDuration,
				ease: "power2.out",
				onComplete: () => {
					if (
						previousDevice.dataset.na6cTabDevice !==
						activeAcademicTab
					) {
						previousDevice.classList.remove("is-active");
						gsap.set(previousDevice, {
							clearProps: "opacity,visibility",
						});
					}
				},
			});
		}

		if (nextDevice) {
			nextDevice.classList.add("is-active");
			gsap.fromTo(
				nextDevice,
				{ autoAlpha: 0 },
				{
					autoAlpha: 1,
					duration: transitionDuration,
					ease: "power2.out",
				},
			);
		}

		academicDeviceTabTween = gsap.to(academicDevice, {
			autoAlpha: nextDevice ? 0 : 1,
			duration: transitionDuration,
			ease: "power2.out",
		});
	};

	academicTabItems.forEach((item) => {
		item.querySelector("[data-na6c-tab-btn]")?.addEventListener(
			"click",
			() => setAcademicTab(item.dataset.na6cTab),
		);
	});

	function createAcademicWorkflow() {
		const workflow = gsap.timeline({
			paused: true,
			onComplete: () => {
				academicSection.classList.add("is-revealed");
				if (academicMobile) return;

				academicTabs.classList.add("is-visible");
				academicTabsReady = true;
				gsap.set(academicTabs, { clearProps: "all" });
				gsap.set(academicDevice, {
					clearProps: "left,opacity,top,transform,visibility,width",
				});
			},
			onReverseComplete: () => {
				if (academicMobile) {
					academicSection.classList.remove("is-revealed");
					gsap.set(academicMovingPanels, {
						clearProps: "opacity,transform,visibility",
					});
					return;
				}

				academicTabsReady = false;
				resetAcademicTabSelection({ restoreDevice: false });
				academicSection.classList.remove("is-revealed");
				academicTabs.classList.remove("is-visible");
				gsap.set(
					[
						academicCopy,
						academicDevice,
						...academicPanels,
						academicTabs,
					],
					{
						clearProps: "all",
					},
				);
			},
		});

		if (academicMobile) {
			return workflow.to(academicMovingPanels, {
				autoAlpha: 0,
				duration: 0.72,
				ease: "power3.in",
				scale: 0.32,
				stagger: 0.04,
				x: (_index, panel) => getAcademicPanelOffset(panel).x,
				y: (_index, panel) => getAcademicPanelOffset(panel).y,
			});
		}

		return workflow
			.fromTo(
				academicDevice,
				{ autoAlpha: 0, scale: 0.9, yPercent: 12 },
				{
					autoAlpha: 1,
					scale: 1,
					yPercent: 0,
					duration: 0.7,
					ease: "power2.out",
				},
			)
			.fromTo(
				academicLeft,
				{ autoAlpha: 0, xPercent: -45, scale: 1.12 },
				{
					autoAlpha: 1,
					xPercent: 0,
					scale: 1,
					duration: 0.55,
					ease: "power2.out",
				},
				"-=0.3",
			)
			.fromTo(
				academicRight,
				{ autoAlpha: 0, xPercent: 45, scale: 1.12 },
				{
					autoAlpha: 1,
					xPercent: 0,
					scale: 1,
					duration: 0.55,
					ease: "power2.out",
				},
				"<",
			)
			.fromTo(
				[academicDocs],
				{ autoAlpha: 0, xPercent: -45, scale: 1.12 },
				{
					autoAlpha: 1,
					scale: 1,
					xPercent: 0,
					duration: 0.5,
					stagger: 0.12,
					ease: "power2.out",
				},
				"<",
			)
			.fromTo(
				[academicSlides],
				{ autoAlpha: 0, xPercent: 45, scale: 1.12 },
				{
					autoAlpha: 1,
					scale: 1,
					xPercent: 0,
					duration: 0.5,
					stagger: 0.12,
					ease: "power2.out",
				},
				"<",
			)
			.addLabel("panels-exit", "+=0.08")
			.to(
				academicMovingPanels,
				{
					autoAlpha: 0,
					duration: 0.52,
					ease: "power3.in",
					scale: 0.32,
					stagger: 0.04,
					x: (_index, panel) => getAcademicPanelOffset(panel).x,
					y: (_index, panel) => getAcademicPanelOffset(panel).y,
				},
				"panels-exit",
			)
			.to(academicDevice, {
				duration: 0.52,
				ease: "power3.inOut",
				left: academicDesktop ? "18.940104%" : "4%",
				top: academicDesktop ? "32.287571%" : "35%",
				width: academicDesktop ? "29.654033%" : "46%",
			})
			.add(() => academicTabs.classList.add("is-visible"), "-=0.18")
			.fromTo(
				academicTabs,
				{ autoAlpha: 0, xPercent: 4 },
				{
					autoAlpha: 1,
					duration: 0.58,
					ease: "power3.out",
					xPercent: 0,
				},
				"<",
			);
	}

	if (academicWorkflowEnabled && !academicReduceMotion) {
		if (academicMobile) {
			academicTabs.classList.add("is-visible");
			academicTabsReady = true;
			// setAcademicTab("1");
		} else {
			gsap.set(academicTabs, {
				autoAlpha: 0,
				pointerEvents: "none",
				xPercent: 4,
			});
		}
		const academicWorkflow = createAcademicWorkflow();
		const completeAcademicWorkflow = () => {
			if (academicWorkflow.progress() < 1) {
				academicWorkflow.progress(1);
			}
		};
		const prepareAcademicReverse = () => {
			const shouldResetTabs =
				academicTabsReady ||
				Boolean(activeAcademicTab) ||
				Boolean(academicDeviceTabTween);

			academicTabsReady = false;
			if (shouldResetTabs) resetAcademicTabSelection();
		};

		ScrollTrigger.create({
			animation: academicWorkflow,
			invalidateOnRefresh: true,
			onEnterBack: completeAcademicWorkflow,
			onLeave: completeAcademicWorkflow,
			start: academicMobile ? "top 10%" : "top 75%",
			toggleActions: "play none none none",
			trigger: academicSection,
		});
		ScrollTrigger.create({
			invalidateOnRefresh: true,
			onLeaveBack: () => {
				if (!academicMobile) prepareAcademicReverse();
				academicWorkflow.reverse();
			},
			start: "top bottom",
			trigger: academicSection,
		});
	} else {
		academicSection.classList.add("is-revealed");
		academicTabs.classList.add("is-visible");
		gsap.set(academicPanels, { autoAlpha: 0 });
		academicTabsReady = true;
		// if (academicMobile) setAcademicTab("1");
	}
}

const knowledgeSection = document.querySelector("[data-na6c-knowledge]");

if (knowledgeSection) {
	const knowledgeStage = knowledgeSection.querySelector(
		"[data-na6c-knowledge-stage-inner]",
	);
	const knowledgeIntro = knowledgeSection.querySelector(
		"[data-na6c-knowledge-intro]",
	);
	const knowledgeIntroExitEffect = createNa6cTextExitEffect(knowledgeSection);
	const knowledgeCardsContainer = knowledgeSection.querySelector(
		"[data-na6c-knowledge-cards]",
	);
	const knowledgeGradient = knowledgeSection.querySelector(
		"[data-na6c-knowledge-gradient]",
	);
	const knowledgeStatus = knowledgeSection.querySelector(
		"[data-na6c-knowledge-status]",
	);
	const knowledgeCards = [
		...knowledgeSection.querySelectorAll("[data-na6c-knowledge-card]"),
	];
	const knowledgeTabletSlider = knowledgeSection.querySelector(
		"[data-na6c-knowledge-tablet-slider]",
	);
	const knowledgeTabletIntro = knowledgeSection.querySelector(
		"[data-na6c-knowledge-tablet-intro]",
	);
	const knowledgeTabletSlides = knowledgeTabletSlider
		? [...knowledgeTabletSlider.querySelectorAll(".splide__slide")]
		: [];
	const knowledgeHeaders = [
		document.querySelector(".wrapper-header"),
		document.querySelector(".product-specs__header"),
	].filter(Boolean);
	const knowledgeLabels = [
		"Knowledge workflow introduction",
		"Distill Intellectual Findings",
		"Map Frameworks",
		"Reference without Limits",
	];
	const knowledgeColors = [
		["#6c5c63", "#8a8085"],
		["#6c5c63", "#8a8085"],
		["#5c5f73", "#696c80"],
		["#68535c", "#77696a"],
	];
	const desktopKnowledgeStates = [
		[
			{ x: -1261, y: -778, rotation: -1, blur: 20, opacity: 0 },
			{ x: 276.818, y: 868.767, rotation: 6.63, opacity: 1 },
			{ x: 1920, y: 993, rotation: 1.2, blur: 15, opacity: 0 },
			{ x: 3300, y: 1780, rotation: -1, blur: 20, opacity: 0 },
			{ x: 4680, y: 2550, rotation: 1, blur: 20, opacity: 0 },
		],
		[
			{ x: -1039.447, y: -475.184, rotation: -0.9, blur: 15, opacity: 1 },
			{
				x: 330.297,
				y: 111.908,
				rotation: -1.354,
				opacity: 1,
				copyOpacity: 1,
			},
			{ x: 1679.961, y: 699.844, rotation: 1.24, blur: 15, opacity: 1 },
			{ x: 3074.977, y: 1488.113, rotation: -0.87, blur: 20, opacity: 1 },
			{ x: 4465, y: 2270, rotation: 1.35, blur: 20, opacity: 0 },
		],
		[
			{ x: -2393.758, y: -1076.789, rotation: -1, blur: 20, opacity: 1 },
			{
				x: -1035.561,
				y: -481.489,
				rotation: -1.354,
				blur: 15,
				opacity: 1,
			},
			{
				x: 316.777,
				y: 112.176,
				rotation: 1.24,
				opacity: 1,
				copyOpacity: 1,
			},
			{ x: 1678.707, y: 697.5, rotation: -0.872, blur: 15, opacity: 1 },
			{ x: 3081.76, y: 1450.832, rotation: 1.35, blur: 20, opacity: 1 },
		],
		[
			{ x: -3710, y: -1870, rotation: -1, blur: 20, opacity: 0 },
			{
				x: -2318.715,
				y: -1279.237,
				rotation: -1.354,
				blur: 20,
				opacity: 1,
			},
			{ x: -1039.871, y: -475.494, rotation: 1.24, blur: 15, opacity: 1 },
			{
				x: 326.653,
				y: 100.414,
				rotation: -0.872,
				opacity: 1,
				copyOpacity: 1,
			},
			{ x: 1679.539, y: 699.533, rotation: 1.354, blur: 15, opacity: 1 },
		],
	];
	const mobileDecorScale = 327.076 / 338.254;
	const mobileKnowledgeStates = [
		[
			{
				x: -430,
				y: -370,
				rotation: 0.5,
				blur: 15,
				opacity: 0,
				scale: mobileDecorScale,
			},
			{
				x: -11.883,
				y: 426,
				rotation: -3,
				opacity: 1,
				scale: 1.1294,
			},
			{ x: 390, y: 670, rotation: -1.2, blur: 15, opacity: 0 },
			{ x: 680, y: 880, rotation: 0.5, blur: 15, opacity: 0 },
			{ x: 950, y: 1100, rotation: 2.9, blur: 15, opacity: 0 },
		],
		[
			{
				x: -211.055,
				y: -156.646,
				rotation: 0.465,
				blur: 10,
				opacity: 1,
				scale: mobileDecorScale,
			},
			{
				x: 12.88,
				y: 138.387,
				rotation: -2,
				opacity: 1,
				copyOpacity: 1,
			},
			{
				x: 252.584,
				y: 533.629,
				rotation: -1.255,
				blur: 10,
				opacity: 1,
				scale: mobileDecorScale,
			},
			{
				x: 520,
				y: 747,
				rotation: 0.465,
				blur: 15,
				opacity: 0,
				scale: mobileDecorScale,
			},
			{
				x: 790,
				y: 960,
				rotation: 2.975,
				blur: 15,
				opacity: 0,
				scale: mobileDecorScale,
			},
		],
		[
			{
				x: -470,
				y: -370,
				rotation: 0.465,
				blur: 15,
				opacity: 0,
				scale: mobileDecorScale,
			},
			{
				x: -211.055,
				y: -156.646,
				rotation: 2.975,
				blur: 10,
				opacity: 1,
				scale: mobileDecorScale,
			},
			{
				x: 17.835,
				y: 125.844,
				rotation: 1.5,
				opacity: 1,
				copyOpacity: 1,
			},
			{
				x: 252.584,
				y: 533.629,
				rotation: 0.465,
				blur: 10,
				opacity: 1,
				scale: mobileDecorScale,
			},
			{
				x: 520,
				y: 747,
				rotation: 2.975,
				blur: 15,
				opacity: 0,
				scale: mobileDecorScale,
			},
		],
		[
			{
				x: -720,
				y: -580,
				rotation: 0.465,
				blur: 15,
				opacity: 0,
				scale: mobileDecorScale,
			},
			{
				x: -480,
				y: -370,
				rotation: 2.975,
				blur: 15,
				opacity: 0,
				scale: mobileDecorScale,
			},
			{
				x: -226.341,
				y: -158.604,
				rotation: -1.255,
				blur: 15,
				opacity: 1,
				scale: mobileDecorScale,
			},
			{
				x: 20.263,
				y: 130.898,
				rotation: -1,
				opacity: 1,
				copyOpacity: 1,
			},
			{
				x: 252.584,
				y: 533.629,
				rotation: 2.975,
				blur: 15,
				opacity: 1,
				scale: mobileDecorScale,
			},
		],
	];
	const createKnowledgeMatchMedia = () => {
		if (typeof gsap.matchMedia === "function") return gsap.matchMedia();

		const entries = [];
		return {
			add(query, callback) {
				const mediaQuery = window.matchMedia(query);
				const entry = { cleanup: null, mediaQuery };
				const sync = () => {
					if (mediaQuery.matches && !entry.cleanup) {
						entry.cleanup = callback() || (() => {});
					} else if (!mediaQuery.matches && entry.cleanup) {
						entry.cleanup();
						entry.cleanup = null;
					}
				};

				entry.sync = sync;
				entries.push(entry);
				sync();
				mediaQuery.addEventListener?.("change", sync);
				mediaQuery.addListener?.(sync);
				return this;
			},
			revert() {
				entries.forEach(({ cleanup, mediaQuery, sync }) => {
					cleanup?.();
					mediaQuery.removeEventListener?.("change", sync);
					mediaQuery.removeListener?.(sync);
				});
			},
		};
	};
	const knowledgeMatchMedia = createKnowledgeMatchMedia();
	let knowledgeStageIndex = -1;
	let knowledgeScrollTween;
	let knowledgeHeaderTween;
	let knowledgeManualScrollActive = false;
	let knowledgePointerActive = false;

	const knowledgeStateVars = (state = {}) => ({
		"--na6c-card-blur": state.blur ?? 0,
		"--na6c-card-copy-opacity": state.copyOpacity ?? 0,
		"--na6c-card-opacity": state.opacity ?? 1,
		"--na6c-card-rotation": state.rotation ?? 0,
		"--na6c-card-scale": state.scale ?? 1,
		"--na6c-card-x": state.x ?? 0,
		"--na6c-card-y": state.y ?? 0,
	});

	const setKnowledgeStage = (nextStage) => {
		const stageIndex = gsap.utils.clamp(0, 3, Math.round(nextStage));
		if (stageIndex === knowledgeStageIndex) return;

		knowledgeStageIndex = stageIndex;
		knowledgeSection.dataset.na6cKnowledgeStage = String(stageIndex);
		knowledgeSection.classList.toggle("is-carousel", stageIndex > 0);
		knowledgeCards.forEach((card) => {
			const cardIndex = Number(card.dataset.na6cKnowledgeIndex);
			const isActive = stageIndex > 0 && cardIndex === stageIndex - 1;
			card.classList.toggle("is-active", isActive);
			card.setAttribute("aria-hidden", String(!isActive));
		});
		if (knowledgeStatus)
			knowledgeStatus.textContent = knowledgeLabels[stageIndex];
	};

	const setKnowledgeHeaderHidden = (
		hidden,
		isMobile,
		{ immediate = false } = {},
	) => {
		if (!knowledgeHeaders.length) return;

		const shouldHide = hidden && isMobile;
		knowledgeHeaderTween?.kill();
		knowledgeHeaderTween = null;

		if (immediate) {
			gsap.set(knowledgeHeaders, {
				autoAlpha: shouldHide ? 0 : 1,
			});
			return;
		}

		knowledgeHeaderTween = gsap.to(knowledgeHeaders, {
			autoAlpha: shouldHide ? 0 : 1,
			duration: 0.4,
			ease: "power2.out",
			overwrite: "auto",
		});
	};

	const clearKnowledgeHeaderState = () => {
		knowledgeHeaderTween?.kill();
		knowledgeHeaderTween = null;
		if (!knowledgeHeaders.length) return;
		gsap.set(knowledgeHeaders, {
			clearProps: "opacity,visibility",
		});
	};

	const buildKnowledgeMotion = (states, isMobile) => {
		knowledgeSection.classList.add("is-motion-ready");
		setKnowledgeStage(0);
		knowledgeIntroExitEffect.reset();
		gsap.set(knowledgeIntro, { autoAlpha: 1 });
		gsap.set(knowledgeSection, { backgroundColor: knowledgeColors[0][0] });
		gsap.set(knowledgeGradient, { backgroundColor: knowledgeColors[0][1] });
		knowledgeCards.forEach((card, index) => {
			gsap.set(card, knowledgeStateVars(states[0][index]));
		});

		const knowledgeDirectionalSnap = ScrollTrigger.snapDirectional(1 / 3);
		const timeline = gsap.timeline({
			defaults: { duration: 1, ease: "power2.inOut" },
			scrollTrigger: {
				trigger: knowledgeSection,
				start: "top top",
				end: () => `+=${Math.max(window.innerHeight * 3.2, 1900)}`,
				pin: true,
				pinSpacing: true,
				scrub: 0.35,
				snap: {
					snapTo: (progress, self) => {
						if (
							knowledgeManualScrollActive ||
							knowledgePointerActive
						)
							return progress;
						return knowledgeDirectionalSnap(
							progress,
							self.direction,
						);
					},
					delay: 0,
					inertia: false,
					duration: { min: 0.24, max: 0.45 },
					ease: "power1.inOut",
				},
				anticipatePin: 1,
				invalidateOnRefresh: true,
				onUpdate: (self) => setKnowledgeStage(self.progress * 3),
				onToggle: (self) =>
					setKnowledgeHeaderHidden(self.isActive, isMobile),
				onRefresh: (self) =>
					setKnowledgeHeaderHidden(self.isActive, isMobile, {
						immediate: true,
					}),
			},
		});

		knowledgeIntroExitEffect.addExit(timeline, knowledgeIntro, {
			position: 0.04,
		});

		for (let stageIndex = 1; stageIndex <= 3; stageIndex += 1) {
			const timelinePosition = stageIndex - 1;
			knowledgeCards.forEach((card, cardIndex) => {
				timeline.to(
					card,
					knowledgeStateVars(states[stageIndex][cardIndex]),
					timelinePosition,
				);
			});
			timeline.to(
				knowledgeSection,
				{ backgroundColor: knowledgeColors[stageIndex][0] },
				timelinePosition,
			);
			timeline.to(
				knowledgeGradient,
				{ backgroundColor: knowledgeColors[stageIndex][1] },
				timelinePosition,
			);
		}

		const knowledgeTrigger = timeline.scrollTrigger;
		const dragState = {
			active: false,
			horizontal: false,
			pointerId: null,
			startX: 0,
			startY: 0,
			startScroll: 0,
			startStep: 0,
		};
		const cancelKnowledgeSnap = () => {
			const snapTween = knowledgeTrigger?.getTween?.(true);
			if (typeof snapTween?.kill === "function") snapTween.kill();
		};
		const finishKnowledgeManualScroll = () => {
			knowledgeScrollTween = null;
			knowledgeManualScrollActive = false;
		};

		const scrollToKnowledgeStep = (step) => {
			if (!knowledgeTrigger || !Number.isFinite(knowledgeTrigger.start))
				return;
			const targetStep = gsap.utils.clamp(0, 3, Math.round(step));
			const destination =
				knowledgeTrigger.start +
				((knowledgeTrigger.end - knowledgeTrigger.start) * targetStep) /
					3;
			cancelKnowledgeSnap();
			knowledgeScrollTween?.kill();
			knowledgeManualScrollActive = true;
			const scrollProxy = { value: window.scrollY };
			knowledgeScrollTween = gsap.to(scrollProxy, {
				value: destination,
				duration: 0.35,
				ease: "power1.out",
				overwrite: true,
				onUpdate: () => window.scrollTo(0, scrollProxy.value),
				onComplete: finishKnowledgeManualScroll,
				onInterrupt: finishKnowledgeManualScroll,
			});
		};
		const leaveKnowledgeByKeyboard = (direction) => {
			cancelKnowledgeSnap();
			knowledgeScrollTween?.kill();
			knowledgeManualScrollActive = false;
			const boundaryOffset = Math.max(48, window.innerHeight * 0.06);
			const destination =
				direction < 0
					? Math.max(0, knowledgeTrigger.start - boundaryOffset)
					: knowledgeTrigger.end + boundaryOffset;
			window.scrollTo(0, destination);
		};

		const isKnowledgeActive = () =>
			window.scrollY >= knowledgeTrigger.start - 2 &&
			window.scrollY <= knowledgeTrigger.end + 2;

		const onPointerDown = (event) => {
			if (event.button !== 0 || !event.isPrimary || !isKnowledgeActive())
				return;
			cancelKnowledgeSnap();
			knowledgeScrollTween?.kill();
			knowledgePointerActive = true;
			dragState.active = true;
			dragState.horizontal = false;
			dragState.pointerId = event.pointerId;
			dragState.startX = event.clientX;
			dragState.startY = event.clientY;
			dragState.startScroll = window.scrollY;
			dragState.startStep = knowledgeTrigger.progress * 3;
			knowledgeCardsContainer.setPointerCapture?.(event.pointerId);
		};

		const onPointerMove = (event) => {
			if (!dragState.active || event.pointerId !== dragState.pointerId)
				return;
			const deltaX = event.clientX - dragState.startX;
			const deltaY = event.clientY - dragState.startY;
			if (!dragState.horizontal) {
				if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
				if (Math.abs(deltaY) > Math.abs(deltaX)) {
					dragState.active = false;
					dragState.pointerId = null;
					knowledgePointerActive = false;
					knowledgeCardsContainer.releasePointerCapture?.(
						event.pointerId,
					);
					return;
				}
				dragState.horizontal = true;
				knowledgeSection.classList.add("is-dragging");
			}

			event.preventDefault();
			const dragWidth = Math.max(knowledgeStage.clientWidth * 0.55, 240);
			const scrollPerPixel =
				(knowledgeTrigger.end - knowledgeTrigger.start) / 3 / dragWidth;
			const nextScroll = gsap.utils.clamp(
				knowledgeTrigger.start,
				knowledgeTrigger.end,
				dragState.startScroll - deltaX * scrollPerPixel,
			);
			window.scrollTo(0, nextScroll);
		};

		const finishPointerDrag = (event) => {
			if (!dragState.active || event.pointerId !== dragState.pointerId)
				return;
			const deltaX = event.clientX - dragState.startX;
			if (dragState.horizontal) {
				event.preventDefault();
				let targetStep = Math.round(knowledgeTrigger.progress * 3);
				if (Math.abs(deltaX) > 42) {
					const directionalStep =
						deltaX < 0
							? Math.floor(dragState.startStep) + 1
							: Math.ceil(dragState.startStep) - 1;
					targetStep =
						deltaX < 0
							? Math.max(targetStep, directionalStep)
							: Math.min(targetStep, directionalStep);
				}
				knowledgePointerActive = false;
				scrollToKnowledgeStep(targetStep);
			} else {
				knowledgePointerActive = false;
			}
			knowledgeCardsContainer.releasePointerCapture?.(event.pointerId);
			dragState.active = false;
			dragState.horizontal = false;
			dragState.pointerId = null;
			knowledgeSection.classList.remove("is-dragging");
		};

		const onPointerCancel = (event) => {
			if (event.pointerId !== dragState.pointerId) return;
			knowledgePointerActive = false;
			dragState.active = false;
			dragState.horizontal = false;
			dragState.pointerId = null;
			knowledgeSection.classList.remove("is-dragging");
		};

		const onKnowledgeKeydown = (event) => {
			if (!isKnowledgeActive()) return;
			const currentStep = Math.round(knowledgeTrigger.progress * 3);
			let targetStep = null;
			if (event.key === "ArrowRight" || event.key === "ArrowDown")
				targetStep = currentStep + 1;
			else if (event.key === "ArrowLeft" || event.key === "ArrowUp")
				targetStep = currentStep - 1;
			else if (event.key === "Home") targetStep = 0;
			else if (event.key === "End") targetStep = 3;

			if (targetStep === null) return;
			if (targetStep < 0 || targetStep > 3) {
				event.preventDefault();
				leaveKnowledgeByKeyboard(targetStep < 0 ? -1 : 1);
				return;
			}
			if (targetStep === currentStep) return;
			event.preventDefault();
			scrollToKnowledgeStep(targetStep);
		};

		if (!isMobile) {
			knowledgeCardsContainer.addEventListener(
				"pointerdown",
				onPointerDown,
			);
			knowledgeCardsContainer.addEventListener(
				"pointermove",
				onPointerMove,
			);
			knowledgeCardsContainer.addEventListener(
				"pointerup",
				finishPointerDrag,
			);
			knowledgeCardsContainer.addEventListener(
				"pointercancel",
				onPointerCancel,
			);
		}
		knowledgeStage.addEventListener("keydown", onKnowledgeKeydown);

		return () => {
			cancelKnowledgeSnap();
			knowledgeScrollTween?.kill();
			knowledgeManualScrollActive = false;
			knowledgePointerActive = false;
			knowledgeTrigger.kill();
			timeline.kill();
			if (!isMobile) {
				knowledgeCardsContainer.removeEventListener(
					"pointerdown",
					onPointerDown,
				);
				knowledgeCardsContainer.removeEventListener(
					"pointermove",
					onPointerMove,
				);
				knowledgeCardsContainer.removeEventListener(
					"pointerup",
					finishPointerDrag,
				);
				knowledgeCardsContainer.removeEventListener(
					"pointercancel",
					onPointerCancel,
				);
			}
			knowledgeStage.removeEventListener("keydown", onKnowledgeKeydown);
			knowledgeSection.classList.remove(
				"is-motion-ready",
				"is-carousel",
				"is-dragging",
			);
			knowledgeIntroExitEffect.reset();
			gsap.set(knowledgeIntro, {
				clearProps: "opacity,visibility",
			});
			clearKnowledgeHeaderState();
		};
	};

	knowledgeMatchMedia.add(
		"(min-width: 1025px) and (prefers-reduced-motion: no-preference)",
		() => buildKnowledgeMotion(desktopKnowledgeStates, false),
	);
	knowledgeMatchMedia.add(
		"(min-width: 751px) and (max-width: 1024px)",
		() => {
			if (
				!knowledgeTabletIntro ||
				!knowledgeTabletSlider ||
				typeof Splide !== "function"
			)
				return;

			const reduceMotion = window.matchMedia(
				"(prefers-reduced-motion: reduce)",
			).matches;
			let tabletActiveIndex = 0;
			let tabletBackgroundTimeline;
			let tabletCarouselActive = null;
			let tabletPinTrigger;
			let tabletSwipe;
			let tabletTimeline;
			const tabletSplide = new Splide(knowledgeTabletSlider, {
				arrows: true,
				drag: false,
				easing: "cubic-bezier(0.22, 1, 0.36, 1)",
				pagination: true,
				perMove: 1,
				perPage: 1,
				rewind: false,
				speed: reduceMotion ? 0 : 720,
				type: "slide",
				waitForTransition: true,
			});
			const finishKnowledgeTabletSwipe = (
				event,
				allowNavigation = true,
			) => {
				if (!tabletSwipe || event.pointerId !== tabletSwipe.id) return;

				const deltaX = event.clientX - tabletSwipe.x;
				const deltaY = event.clientY - tabletSwipe.y;
				const swipeThreshold = Math.max(
					44,
					knowledgeTabletSlider.clientWidth * 0.07,
				);

				if (
					allowNavigation &&
					Math.abs(deltaX) >= swipeThreshold &&
					Math.abs(deltaX) > Math.abs(deltaY) * 1.1
				) {
					tabletSplide.go(deltaX < 0 ? ">" : "<");
				}

				if (
					knowledgeTabletSlider.hasPointerCapture?.(event.pointerId)
				) {
					knowledgeTabletSlider.releasePointerCapture(
						event.pointerId,
					);
				}
				knowledgeTabletSlider.classList.remove("is-dragging");
				tabletSwipe = null;
			};
			const onKnowledgeTabletPointerDown = (event) => {
				if (
					!tabletCarouselActive ||
					event.button !== 0 ||
					event.isPrimary === false ||
					event.target.closest?.("button")
				) {
					return;
				}

				tabletSwipe = {
					id: event.pointerId,
					x: event.clientX,
					y: event.clientY,
				};
				knowledgeTabletSlider.setPointerCapture?.(event.pointerId);
			};
			const onKnowledgeTabletPointerMove = (event) => {
				if (!tabletSwipe || event.pointerId !== tabletSwipe.id) return;

				const deltaX = event.clientX - tabletSwipe.x;
				const deltaY = event.clientY - tabletSwipe.y;
				if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;

				if (Math.abs(deltaY) > Math.abs(deltaX)) {
					finishKnowledgeTabletSwipe(event, false);
					return;
				}

				event.preventDefault();
				knowledgeTabletSlider.classList.add("is-dragging");
			};
			const onKnowledgeTabletPointerUp = (event) => {
				finishKnowledgeTabletSwipe(event);
			};
			const onKnowledgeTabletPointerCancel = (event) => {
				finishKnowledgeTabletSwipe(event, false);
			};
			const setKnowledgeTabletBackground = (
				nextIndex,
				{ immediate = false } = {},
			) => {
				const colorIndex = gsap.utils.clamp(1, 3, nextIndex + 1);
				tabletBackgroundTimeline?.kill();
				tabletBackgroundTimeline = null;

				if (immediate || reduceMotion) {
					gsap.set(knowledgeSection, {
						backgroundColor: knowledgeColors[colorIndex][0],
					});
					gsap.set(knowledgeGradient, {
						backgroundColor: knowledgeColors[colorIndex][1],
					});
					return;
				}

				tabletBackgroundTimeline = gsap
					.timeline({
						defaults: {
							duration: 0.55,
							ease: "power2.inOut",
							overwrite: "auto",
						},
					})
					.to(
						knowledgeSection,
						{ backgroundColor: knowledgeColors[colorIndex][0] },
						0,
					)
					.to(
						knowledgeGradient,
						{ backgroundColor: knowledgeColors[colorIndex][1] },
						0,
					);
			};
			const syncKnowledgeTabletScene = (progress) => {
				const carouselActive = reduceMotion || progress >= 0.5;
				if (carouselActive !== tabletCarouselActive) {
					tabletCarouselActive = carouselActive;
					gsap.set(knowledgeTabletSlider, {
						pointerEvents: carouselActive ? "auto" : "none",
					});
					knowledgeTabletIntro.setAttribute(
						"aria-hidden",
						String(carouselActive),
					);
					knowledgeTabletSlider.setAttribute(
						"aria-hidden",
						String(!carouselActive),
					);
					knowledgeTabletSlider.toggleAttribute(
						"inert",
						!carouselActive,
					);
					setKnowledgeTabletBackground(
						carouselActive ? tabletActiveIndex : 0,
					);
				}

				knowledgeStageIndex = carouselActive
					? tabletActiveIndex + 1
					: 0;
				knowledgeSection.dataset.na6cKnowledgeStage =
					String(knowledgeStageIndex);
				if (knowledgeStatus) {
					knowledgeStatus.textContent = carouselActive
						? knowledgeLabels[tabletActiveIndex + 1]
						: knowledgeLabels[0];
				}
			};
			const setKnowledgeTabletSlide = (nextIndex) => {
				const activeIndex = gsap.utils.clamp(0, 2, nextIndex);
				tabletActiveIndex = activeIndex;
				knowledgeTabletSlides.forEach((slide, index) => {
					const isActive = index === activeIndex;
					slide.classList.toggle("is-active", isActive);
					slide.setAttribute("aria-hidden", String(!isActive));
				});
				syncKnowledgeTabletScene(tabletTimeline?.progress() ?? 0);
			};

			knowledgeSection.classList.add("is-tablet-carousel");
			clearKnowledgeHeaderState();
			gsap.set(knowledgeSection, {
				backgroundColor: knowledgeColors[0][0],
			});
			gsap.set(knowledgeGradient, {
				backgroundColor: knowledgeColors[0][1],
			});
			gsap.set(knowledgeTabletIntro, {
				autoAlpha: reduceMotion ? 0 : 1,
				y: 0,
			});
			knowledgeIntroExitEffect.reset();
			gsap.set(knowledgeTabletSlider, {
				autoAlpha: reduceMotion ? 1 : 0,
				pointerEvents: reduceMotion ? "auto" : "none",
				y: reduceMotion ? 0 : 68,
			});
			tabletSplide.on("mounted", () => {
				setKnowledgeTabletSlide(0);
				setKnowledgeTabletBackground(0, { immediate: true });
			});
			tabletSplide.on("move", (nextIndex) =>
				setKnowledgeTabletBackground(nextIndex),
			);
			tabletSplide.on("moved", setKnowledgeTabletSlide);
			tabletSplide.mount();
			knowledgeTabletSlider.addEventListener(
				"pointerdown",
				onKnowledgeTabletPointerDown,
			);
			knowledgeTabletSlider.addEventListener(
				"pointermove",
				onKnowledgeTabletPointerMove,
			);
			knowledgeTabletSlider.addEventListener(
				"pointerup",
				onKnowledgeTabletPointerUp,
			);
			knowledgeTabletSlider.addEventListener(
				"pointercancel",
				onKnowledgeTabletPointerCancel,
			);

			if (reduceMotion) {
				syncKnowledgeTabletScene(1);
			} else {
				tabletTimeline = gsap.timeline({ paused: true });
				tabletTimeline.eventCallback("onUpdate", () =>
					syncKnowledgeTabletScene(tabletTimeline.progress()),
				);
				knowledgeIntroExitEffect.addExit(
					tabletTimeline,
					knowledgeTabletIntro,
					{ duration: 0.72, position: 0 },
				);
				tabletTimeline
					.fromTo(
						knowledgeTabletSlider,
						{
							autoAlpha: 0,
							y: 68,
						},
						{
							autoAlpha: 1,
							duration: 0.68,
							ease: "power2.out",
							y: 0,
						},
						">",
					)
					.to(
						knowledgeTabletSlider,
						{ autoAlpha: 1, duration: 0.24 },
						">",
					);

				const completeKnowledgeTabletScene = () => {
					if (tabletTimeline.progress() < 1) {
						tabletTimeline.progress(1);
					}
				};
				tabletPinTrigger = ScrollTrigger.create({
					animation: tabletTimeline,
					anticipatePin: 1,
					end: () => `+=${Math.max(window.innerHeight * 0.72, 520)}`,
					invalidateOnRefresh: true,
					onEnterBack: completeKnowledgeTabletScene,
					onLeave: completeKnowledgeTabletScene,
					onLeaveBack: () => tabletTimeline.reverse(),
					onRefresh: () =>
						syncKnowledgeTabletScene(tabletTimeline.progress()),
					pin: true,
					pinSpacing: true,
					start: "top top",
					toggleActions: "play none none none",
					trigger: knowledgeSection,
				});
				syncKnowledgeTabletScene(tabletTimeline.progress());
			}

			return () => {
				tabletBackgroundTimeline?.kill();
				tabletPinTrigger?.kill();
				tabletTimeline?.kill();
				knowledgeTabletSlider.removeEventListener(
					"pointerdown",
					onKnowledgeTabletPointerDown,
				);
				knowledgeTabletSlider.removeEventListener(
					"pointermove",
					onKnowledgeTabletPointerMove,
				);
				knowledgeTabletSlider.removeEventListener(
					"pointerup",
					onKnowledgeTabletPointerUp,
				);
				knowledgeTabletSlider.removeEventListener(
					"pointercancel",
					onKnowledgeTabletPointerCancel,
				);
				if (
					tabletSwipe &&
					knowledgeTabletSlider.hasPointerCapture?.(tabletSwipe.id)
				) {
					knowledgeTabletSlider.releasePointerCapture(tabletSwipe.id);
				}
				knowledgeTabletSlider.classList.remove("is-dragging");
				tabletSwipe = null;
				tabletSplide.destroy(true);
				knowledgeStageIndex = -1;
				knowledgeSection.dataset.na6cKnowledgeStage = "0";
				knowledgeSection.classList.remove(
					"is-carousel",
					"is-tablet-carousel",
				);
				gsap.set(knowledgeTabletIntro, {
					clearProps: "opacity,transform,visibility",
				});
				knowledgeIntroExitEffect.reset();
				gsap.set(knowledgeTabletSlider, {
					clearProps: "opacity,transform,visibility,pointer-events",
				});
				gsap.set(knowledgeSection, { clearProps: "background-color" });
				gsap.set(knowledgeGradient, { clearProps: "background-color" });
				knowledgeTabletIntro.setAttribute("aria-hidden", "true");
				knowledgeTabletSlider.setAttribute("aria-hidden", "true");
				knowledgeTabletSlider.setAttribute("inert", "");
				knowledgeTabletSlides.forEach((slide) => {
					slide.classList.remove("is-active");
					slide.removeAttribute("aria-hidden");
				});
			};
		},
	);
	knowledgeMatchMedia.add(
		"(max-width: 750px) and (prefers-reduced-motion: no-preference)",
		() => buildKnowledgeMotion(mobileKnowledgeStates, true),
	);
	knowledgeMatchMedia.add("(prefers-reduced-motion: reduce)", () => {
		if (window.innerWidth > 750 && window.innerWidth <= 1024) return;
		setKnowledgeStage(1);
		knowledgeIntroExitEffect.reset();
		gsap.set(knowledgeIntro, { autoAlpha: 0 });
		knowledgeCards.forEach((card, index) => {
			gsap.set(
				card,
				knowledgeStateVars(
					(window.innerWidth <= 750
						? mobileKnowledgeStates
						: desktopKnowledgeStates)[1][index],
				),
			);
		});
	});
}

function getBrowserInfo() {
	const ua = navigator.userAgent;
	const platform = navigator.platform || "";
	const maxTouchPoints = navigator.maxTouchPoints || 0;

	let browserName = "Unknown";
	let engine = "Unknown";
	let os = "Unknown";
	let devicePlatform = "desktop";
	const touchscreen = maxTouchPoints > 0;

	const isIPad =
		/iPad/.test(ua) ||
		(platform === "MacIntel" &&
			maxTouchPoints > 1 &&
			!/iPhone|iPod/.test(ua));

	if (/iPhone OS/.test(ua) || isIPad) {
		os = "iOS";
	} else if (/Windows NT/.test(ua)) {
		os = "Windows";
	} else if (/Mac OS X/.test(ua)) {
		os = "macOS";
	} else if (/Android/.test(ua)) {
		os = "Android";
	} else if (/CrOS/.test(ua)) {
		os = "Chrome OS";
	} else if (/Linux/.test(ua)) {
		os = "Linux";
	}

	if (os === "iOS") {
		engine = "WebKit";
	} else if (
		/Edg|OPR|Chrome|SamsungBrowser|YaBrowser|Vivaldi|CriOS/.test(ua)
	) {
		engine = "Blink";
	} else if (/Firefox|FxiOS/.test(ua)) {
		engine = "Gecko";
	} else if (
		/Safari/.test(ua) &&
		!/Chrome|CriOS|FxiOS|EdgiOS|OPiOS|Android/.test(ua)
	) {
		engine = "WebKit";
	} else if (/Trident|MSIE/.test(ua)) {
		engine = "Trident";
	}

	// Check embedded and Chromium-derived browsers before generic Chrome/Safari.
	if (/MicroMessenger\//.test(ua)) {
		browserName = "WeChat WebView";
	} else if (/UCBrowser\//.test(ua)) {
		browserName = "UC Browser";
	} else if (/SamsungBrowser\//.test(ua)) {
		browserName = "Samsung Internet";
	} else if (/YaBrowser\//.test(ua)) {
		browserName = "Yandex Browser";
	} else if (/Vivaldi\//.test(ua)) {
		browserName = "Vivaldi";
	} else if (/Edg\//.test(ua) || /EdgiOS/.test(ua)) {
		browserName = "Edge";
	} else if (/OPR\//.test(ua) || /OPiOS/.test(ua)) {
		browserName = "Opera";
	} else if (/Firefox\//.test(ua) || /FxiOS/.test(ua)) {
		browserName = "Firefox";
	} else if (
		(/Chrome\//.test(ua) || /CriOS/.test(ua)) &&
		!/Edg|OPR|SamsungBrowser|YaBrowser|Vivaldi|EdgiOS|OPiOS/.test(ua)
	) {
		browserName = "Chrome";
	} else if (
		/Safari\//.test(ua) &&
		!/Chrome|CriOS|FxiOS|EdgiOS|OPiOS|Android/.test(ua)
	) {
		browserName = "Safari";
	} else if (/Trident|MSIE/.test(ua)) {
		browserName = "Internet Explorer";
	}

	const isAndroidTablet = /Android/.test(ua) && !/Mobile/.test(ua);
	const isTablet =
		isIPad || isAndroidTablet || /Tablet|PlayBook|Silk/i.test(ua);

	if (isTablet) {
		devicePlatform = "tablet";
	} else if (
		/Mobi|Android|iPhone|iPad|iPod|Windows Phone|IEMobile|Opera Mini/i.test(
			ua,
		)
	) {
		devicePlatform = "mobile";
	}

	return {
		browserName,
		platform: devicePlatform,
		os,
		touchscreen,
		engine,
	};
}

const noteToolsSlider = document.getElementById("na6c-note-tools-splide");

const lassoVideo = noteToolsSlider?.querySelector("[data-na6c-lasso-video]");

if (lassoVideo) {
	const source = lassoVideo.querySelector("source");
	const { engine } = getBrowserInfo();
	const webmVideo = {
		src: lassoVideo.dataset.webmSrc,
		type: 'video/webm; codecs="vp9, opus"',
	};
	const webkitVideo = {
		src: lassoVideo.dataset.webkitSrc,
		type: 'video/quicktime; codecs="hvc1"',
	};
	const candidates =
		engine === "WebKit"
			? [webkitVideo, webmVideo]
			: [webmVideo, webkitVideo];
	let fallbackUsed = false;

	const loadCandidate = ({ src, type }) => {
		if (!source || !src) return;

		source.src = src;
		source.type = type;
		lassoVideo.muted = true;
		lassoVideo.load();
		lassoVideo.play().catch(() => {
			// Autoplay can be blocked by user preferences; loading may still succeed.
		});
	};

	const loadFallback = () => {
		if (fallbackUsed) return;
		fallbackUsed = true;
		loadCandidate(candidates[1]);
	};

	source?.addEventListener("error", loadFallback);
	lassoVideo.addEventListener("error", loadFallback);
	loadCandidate(candidates[0]);
}

if (noteToolsSlider) {
	new Splide(noteToolsSlider, {
		type: "slide",
		fixedWidth: "calc(var(--count-width) * 506.342)",
		gap: "calc(var(--count-width) * 32)",
		padding: {
			left: "calc(var(--count-width) * 467.4)",
			right: "calc(var(--count-width) * 240)",
		},
		perMove: 1,
		pagination: false,
		arrows: true,
		breakpoints: {
			1024: {
				fixedWidth: "calc(var(--count-width) * 381.5)",
				gap: "calc(var(--count-width) * 14)",
				padding: {
					left: "calc(var(--count-width) * 116)",
					right: "calc(var(--count-width) * 116)",
				},
			},
			750: {
				fixedWidth: "calc(var(--count-width) * 544)",
				gap: "calc(var(--count-width) * 24)",
				padding: {
					left: "calc(var(--count-width) * 32)",
					right: "calc(var(--count-width) * 32)",
				},
			},
		},
	}).mount();

	if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
		gsap.from(noteToolsSlider.querySelectorAll(".splide__slide"), {
			autoAlpha: 0,
			yPercent: 12,
			duration: 0.7,
			stagger: 0.1,
			ease: "power2.out",
			scrollTrigger: {
				trigger: noteToolsSlider,
				start: "top 75%",
				once: true,
			},
		});
	}
}

const talkSection = document.querySelector(".na6c-talk");
const talkTabs = talkSection
	? [...talkSection.querySelectorAll("[data-na6c-talk-tab]")]
	: [];
const talkPanels = talkSection
	? [...talkSection.querySelectorAll("[data-na6c-talk-panel]")]
	: [];
const talkReduceMotion = window.matchMedia(
	"(prefers-reduced-motion: reduce)",
).matches;
let talkIntroTimeline;
let talkPanelTransition;

const getTalkPanelItems = (panel) =>
	panel ? [...panel.querySelectorAll("img, p")] : [];

const resetTalkPanelMotion = () => {
	talkPanels.forEach((panel) => {
		gsap.set(getTalkPanelItems(panel), {
			clearProps: "opacity,visibility,transform",
		});
	});
};

const showTalkPanel = (nextPanel) => {
	talkPanels.forEach((panel) => {
		const isActive = panel === nextPanel;
		panel.classList.toggle("is-active", isActive);
		panel.hidden = !isActive;
	});

	if (talkSection && nextPanel?.dataset.na6cTalkPanel) {
		talkSection.dataset.na6cTalkActive = nextPanel.dataset.na6cTalkPanel;
	}
};

const drawTalkTab = (tab, restart = true) => {
	const draw = tab?.querySelector("[data-na6c-talk-tab-draw]");
	if (!draw) return;
	if (draw.classList.contains("is-drawn")) return;

	if (restart && !talkReduceMotion) {
		draw.classList.remove("is-drawn");
		void draw.offsetWidth;
	}

	draw.classList.add("is-drawn");
};

const activateTalkTab = (nextTab, { focus = false, draw = true } = {}) => {
	const nextPanelName = nextTab?.dataset.na6cTalkTab;
	const nextPanel = talkPanels.find(
		(panel) => panel.dataset.na6cTalkPanel === nextPanelName,
	);
	if (!nextPanelName || !nextPanel) return;

	talkTabs.forEach((tab) => {
		const isActive = tab === nextTab;
		tab.classList.toggle("is-active", isActive);
		tab.setAttribute("aria-selected", String(isActive));
		tab.tabIndex = isActive ? 0 : -1;
		if (!isActive) {
			tab.querySelector("[data-na6c-talk-tab-draw]")?.classList.remove(
				"is-drawn",
			);
		}
	});

	if (draw) drawTalkTab(nextTab);
	if (focus) nextTab.focus();

	const wasTransitioning = Boolean(talkPanelTransition);
	if (talkPanelTransition) {
		talkPanelTransition.kill();
		talkPanelTransition = null;
	}
	if (wasTransitioning) resetTalkPanelMotion();

	const currentPanel = talkPanels.find((panel) => !panel.hidden);
	if (!currentPanel || currentPanel === nextPanel) {
		showTalkPanel(nextPanel);
		return;
	}

	if (talkIntroTimeline) {
		talkIntroTimeline.scrollTrigger?.kill();
		talkIntroTimeline.kill();
		talkIntroTimeline = null;
		gsap.set(talkSection.querySelector(".na6c-talk__tabs"), {
			clearProps: "opacity,visibility,transform",
		});
	}
	resetTalkPanelMotion();

	if (talkReduceMotion) {
		showTalkPanel(nextPanel);
		return;
	}

	const currentItems = getTalkPanelItems(currentPanel);
	const nextItems = getTalkPanelItems(nextPanel);

	talkSection.classList.add("is-switching");
	talkPanelTransition = gsap
		.timeline({
			onComplete: () => {
				resetTalkPanelMotion();
				talkSection.classList.remove("is-switching");
				talkPanelTransition = null;
			},
			onInterrupt: () => {
				talkSection.classList.remove("is-switching");
			},
		})
		.to(currentItems, {
			autoAlpha: 0,
			duration: 0.22,
			ease: "power1.in",
			stagger: 0.025,
			yPercent: -2,
		})
		.add(() => {
			gsap.set(currentItems, {
				clearProps: "opacity,visibility,transform",
			});
			showTalkPanel(nextPanel);
		})
		.fromTo(
			nextItems,
			{ autoAlpha: 0, yPercent: 2 },
			{
				autoAlpha: 1,
				duration: 0.42,
				ease: "power2.out",
				stagger: 0.05,
				yPercent: 0,
			},
		);
};

talkTabs.forEach((tab, tabIndex) => {
	tab.addEventListener("click", () => activateTalkTab(tab));
	tab.addEventListener("keydown", (event) => {
		let nextIndex;

		if (event.key === "ArrowRight")
			nextIndex = (tabIndex + 1) % talkTabs.length;
		if (event.key === "ArrowLeft") {
			nextIndex = (tabIndex - 1 + talkTabs.length) % talkTabs.length;
		}
		if (event.key === "Home") nextIndex = 0;
		if (event.key === "End") nextIndex = talkTabs.length - 1;
		if (nextIndex === undefined) return;

		event.preventDefault();
		activateTalkTab(talkTabs[nextIndex], { focus: true });
	});
});

if (talkReduceMotion) {
	drawTalkTab(
		talkTabs.find((tab) => tab.classList.contains("is-active")),
		false,
	);
}

if (talkSection && !talkReduceMotion) {
	talkIntroTimeline = gsap
		.timeline({
			scrollTrigger: {
				trigger: talkSection,
				start: "top 72%",
				once: true,
			},
		})
		.add(() =>
			drawTalkTab(
				talkTabs.find((tab) => tab.classList.contains("is-active")),
			),
		)
		.from(
			talkSection.querySelector(".na6c-talk__content img"),
			{ autoAlpha: 0, xPercent: -10, duration: 0.65, ease: "power2.out" },
			"-=0.2",
		)
		.from(
			talkSection.querySelector(".na6c-talk__content > p"),
			{ autoAlpha: 0, xPercent: 10, duration: 0.65, ease: "power2.out" },
			"<",
		);
}

const readingSection = document.querySelector("[data-na6c-reading]");

if (readingSection) {
	const readingCards = [
		...readingSection.querySelectorAll("[data-na6c-reading-card]"),
	];
	const readingTabs = [
		...readingSection.querySelectorAll("[data-na6c-reading-tab]"),
	];
	const readingCopy = readingSection.querySelector(
		"[data-na6c-reading-copy]",
	);
	const readingStage = readingSection.querySelector(
		"[data-na6c-reading-stage]",
	);
	const readingReduceMotion = window.matchMedia(
		"(prefers-reduced-motion: reduce)",
	).matches;
	let readingScrollTrigger = null;

	const setReadingActive = (activeIndex) => {
		readingCards.forEach((card, cardIndex) => {
			const isActive = cardIndex === activeIndex;
			const tab = readingTabs[cardIndex];

			card.classList.toggle("is-active", isActive);
			card.classList.toggle("is-past", cardIndex < activeIndex);
			card.classList.toggle("is-far-past", cardIndex < activeIndex - 1);
			tab?.setAttribute("aria-pressed", String(isActive));
			if (tab) tab.tabIndex = cardIndex <= activeIndex ? 0 : -1;
		});
	};

	setReadingActive(0);

	if (readingReduceMotion) {
		readingCards[0]?.classList.add("is-strokes-drawn");
	} else {
		ScrollTrigger.create({
			trigger: readingCards[0],
			start: "top 50%",
			once: true,
			onEnter: () => readingCards[0]?.classList.add("is-strokes-drawn"),
		});
	}

	readingTabs.forEach((tab, tabIndex) => {
		tab.addEventListener("click", () => {
			if (
				readingScrollTrigger &&
				Number.isFinite(readingScrollTrigger.start) &&
				Number.isFinite(readingScrollTrigger.end)
			) {
				const scrollRange =
					readingScrollTrigger.end - readingScrollTrigger.start;
				const progress =
					tabIndex / Math.max(readingCards.length - 1, 1);
				const target = Math.min(
					readingScrollTrigger.end - 1,
					readingScrollTrigger.start + scrollRange * progress,
				);

				window.scrollTo({
					behavior: readingReduceMotion ? "auto" : "smooth",
					top: Math.max(0, target),
				});
				return;
			}

			setReadingActive(tabIndex);
			readingCards[tabIndex]?.scrollIntoView({
				behavior: readingReduceMotion ? "auto" : "smooth",
				block: "center",
			});
		});
	});

	const readingMedia = gsap.matchMedia
		? gsap.matchMedia()
		: {
				add: (query, callback) => {
					const mediaQuery = window.matchMedia(query);
					let cleanup;
					const sync = () => {
						if (mediaQuery.matches && !cleanup) {
							cleanup = callback() || (() => {});
						} else if (!mediaQuery.matches && cleanup) {
							cleanup();
							cleanup = undefined;
						}
					};

					sync();
					if (mediaQuery.addEventListener) {
						mediaQuery.addEventListener("change", sync);
					} else {
						mediaQuery.addListener?.(sync);
					}
				},
			};

	const createReadingTimeline =
		({
			hideReadingCopy,
			initialCardY = 25.368,
			pastCardY = -14.241,
			entryGap = 80,
		}) =>
		() => {
			const readingStageBase = () =>
				Number.parseFloat(
					getComputedStyle(readingSection).getPropertyValue(
						"--na6c-reading-stage-base",
					),
				) || 1440;
			const getReadingScale = () =>
				(readingStage?.getBoundingClientRect().width ||
					readingStageBase()) / readingStageBase();
			const getReadingEntryY = () =>
				readingSection.clientHeight + entryGap * getReadingScale();

			const readingTimeline = gsap.timeline({
				scrollTrigger: {
					anticipatePin: 1,
					end: () => `+=${readingSection.clientHeight * 2.4}`,
					invalidateOnRefresh: true,
					onUpdate: (self) => {
						setReadingActive(
							Math.min(
								readingCards.length - 1,
								Math.round(
									self.progress * (readingCards.length - 1),
								),
							),
						);
					},
					pin: true,
					scrub: 0.75,
					start: "top top",
					trigger: readingSection,
				},
			});

			readingTimeline
				.fromTo(
					readingCards[0],
					{ y: () => initialCardY * getReadingScale() },
					{ duration: 1, ease: "power2.inOut", y: 0 },
					0,
				)
				.fromTo(
					readingCards[1],
					{ y: getReadingEntryY },
					{ duration: 1, ease: "power2.out", y: 0 },
					0,
				)
				.to(
					readingCards[1],
					{
						duration: 1,
						ease: "power2.inOut",
						y: () => pastCardY * getReadingScale(),
					},
					1,
				)
				.fromTo(
					readingCards[2],
					{ y: getReadingEntryY },
					{ duration: 1, ease: "power2.out", y: 0 },
					1,
				);

			if (hideReadingCopy) {
				readingTimeline.to(
					readingCopy,
					{ autoAlpha: 0, duration: 0.44, ease: "power1.out" },
					0.04,
				);
			}

			readingScrollTrigger = readingTimeline.scrollTrigger;

			return () => {
				readingScrollTrigger = null;
				readingTimeline.scrollTrigger?.kill();
				readingTimeline.kill();
				gsap.set([...readingCards, readingCopy], {
					clearProps: "opacity,transform,visibility",
				});
				setReadingActive(0);
			};
		};

	readingMedia.add(
		"(min-width: 1025px) and (prefers-reduced-motion: no-preference)",
		createReadingTimeline({ hideReadingCopy: true }),
	);
	readingMedia.add(
		"(min-width: 751px) and (max-width: 1024px) and (prefers-reduced-motion: no-preference)",
		createReadingTimeline({
			entryGap: 42.667,
			hideReadingCopy: false,
			initialCardY: 13.53,
			pastCardY: 0,
		}),
	);
	readingMedia.add(
		"(max-width: 750px) and (prefers-reduced-motion: no-preference)",
		() => {
			const readingCardsContainer = readingSection.querySelector(
				".na6c-reading__cards",
			);
			const getMobileScale = () =>
				readingSection.getBoundingClientRect().width / 375;
			const getMobileCardStep = () => 34.65 * getMobileScale();
			const getMobileEntryY = () =>
				Math.max(
					window.innerHeight,
					readingCardsContainer.clientHeight,
				) + getMobileCardStep();

			const readingTimeline = gsap.timeline({
				scrollTrigger: {
					anticipatePin: 1,
					end: () => `+=${window.innerHeight * 2}`,
					invalidateOnRefresh: true,
					onUpdate: (self) => {
						setReadingActive(
							Math.min(
								readingCards.length - 1,
								Math.round(
									self.progress * (readingCards.length - 1),
								),
							),
						);
					},
					pin: readingCardsContainer,
					scrub: 0.75,
					start: () => `top 100px`,
					trigger: readingCards[0],
				},
			});

			readingTimeline
				.fromTo(
					readingCards[1],
					{ y: getMobileEntryY },
					{ duration: 1, ease: "power2.out", y: getMobileCardStep },
					0,
				)
				.fromTo(
					readingCards[2],
					{ y: getMobileEntryY },
					{
						duration: 1,
						ease: "power2.out",
						y: () => getMobileCardStep() * 2,
					},
					1,
				);

			readingScrollTrigger = readingTimeline.scrollTrigger;

			return () => {
				readingScrollTrigger = null;
				readingTimeline.scrollTrigger?.kill();
				readingTimeline.kill();
				gsap.set(readingCards, { clearProps: "transform" });
				setReadingActive(0);
			};
		},
	);
}

const studyHubItems = document.querySelectorAll(".na6c-study-hub__item");

if (
	studyHubItems.length &&
	!window.matchMedia("(prefers-reduced-motion: reduce)").matches
) {
	gsap.from(studyHubItems, {
		autoAlpha: 0,
		yPercent: 9,
		duration: 0.75,
		stagger: 0.12,
		ease: "power2.out",
		scrollTrigger: {
			trigger: ".na6c-study-hub__grid",
			start: "top 78%",
			once: true,
		},
	});
}

const workflowRows = document.querySelectorAll(".na6c-workflow__row");

if (
	workflowRows.length &&
	!window.matchMedia("(prefers-reduced-motion: reduce)").matches
) {
	workflowRows.forEach((row, index) => {
		const visual = row.querySelector(".na6c-workflow__visual");
		const copy = row.querySelector(".na6c-workflow__copy");
		const fromLeft = index % 2 === 0;

		gsap.timeline({
			scrollTrigger: {
				trigger: row,
				start: "top 80%",
				once: true,
			},
		})
			.from(visual, {
				autoAlpha: 0,
				xPercent: fromLeft ? -8 : 8,
				duration: 0.5,
				ease: "power2.out",
			})
			.from(
				copy,
				{
					autoAlpha: 0,
					xPercent: fromLeft ? 8 : -8,
					duration: 0.4,
					ease: "power2.out",
				},
				"-=0.45",
			);
	});
}

const accessoriesSection = document.querySelector(".na6c-accessories");
const accessoriesTabs = accessoriesSection
	? [...accessoriesSection.querySelectorAll("[data-na6c-accessories-tab]")]
	: [];
const accessoriesPanels = accessoriesSection
	? [...accessoriesSection.querySelectorAll("[data-na6c-accessories-panel]")]
	: [];
const accessoriesReduceMotion = window.matchMedia(
	"(prefers-reduced-motion: reduce)",
).matches;
const accessoriesVideos = accessoriesSection
	? [...accessoriesSection.querySelectorAll("[data-na6c-accessories-video]")]
	: [];
const accessoriesVideoUserAgent = window.navigator.userAgent;
const accessoriesVideoIsIOS =
	/iPad|iPhone|iPod/.test(accessoriesVideoUserAgent) ||
	(window.navigator.platform === "MacIntel" &&
		window.navigator.maxTouchPoints > 1);
const accessoriesVideoIsSafari =
	/Safari\//.test(accessoriesVideoUserAgent) &&
	!/(Chrome|Chromium|CriOS|Edg|EdgiOS|OPR|OPiOS|FxiOS)\//.test(
		accessoriesVideoUserAgent,
	);
const accessoriesVideoFormatOrder =
	accessoriesVideoIsSafari || accessoriesVideoIsIOS
		? ["mov", "webm"]
		: ["webm", "mov"];

const setupAccessoriesVideoSource = (video) => {
	const sources = accessoriesVideoFormatOrder
		.map((format) => ({
			format,
			src: video.dataset[
				`video${format.charAt(0).toUpperCase()}${format.slice(1)}`
			],
		}))
		.filter(({ src }) => src);
	let activeSourceIndex = 0;

	const loadSource = (sourceIndex, shouldPlay = video.autoplay) => {
		const source = sources[sourceIndex];
		if (!source || video.src === source.src) return;

		video.dataset.na6cVideoFormat = source.format;
		video.src = source.src;
		video.load();

		if (shouldPlay) {
			const playPromise = video.play();
			if (playPromise?.catch) playPromise.catch(() => {});
		}
	};
	const loadFallbackSource = () => {
		if (activeSourceIndex >= sources.length - 1) return;

		const shouldResume = video.autoplay || (!video.paused && !video.ended);
		activeSourceIndex += 1;
		loadSource(activeSourceIndex, shouldResume);
	};

	video.addEventListener("error", loadFallbackSource);
	loadSource(activeSourceIndex);
};

accessoriesVideos.forEach(setupAccessoriesVideoSource);
const accessoriesKeyboardVideo = accessoriesSection?.querySelector(
	".na6c-accessories__visual--keyboard [data-na6c-accessories-video]",
);
const accessoriesPenVideo = accessoriesSection?.querySelector(
	".na6c-accessories__visual--pen [data-na6c-accessories-video]",
);
const accessoriesPenCallout = accessoriesSection?.querySelector(
	"[data-na6c-accessories-pen-callout]",
);
let accessoriesIntroTimeline;
let accessoriesPanelTransition;
let accessoriesKeyboardInView = !("IntersectionObserver" in window);
let accessoriesKeyboardAwaitingView = false;

const playAccessoriesVideo = (video) => {
	const playPromise = video?.play();
	if (playPromise?.catch) playPromise.catch(() => {});
};

const syncAccessoriesMedia = (activePanel) => {
	accessoriesVideos.forEach((video) => {
		const isActive = Boolean(activePanel?.contains(video));
		if (!isActive) {
			video.pause();
			if (video.readyState) video.currentTime = 0;
			if (video === accessoriesKeyboardVideo)
				accessoriesKeyboardAwaitingView = false;
			if (video === accessoriesPenVideo)
				accessoriesPenCallout?.classList.remove("is-visible");
			return;
		}

		if (video.readyState) video.currentTime = 0;
		if (video === accessoriesKeyboardVideo && !accessoriesKeyboardInView) {
			video.pause();
			accessoriesKeyboardAwaitingView = true;
			return;
		}
		if (video === accessoriesKeyboardVideo)
			accessoriesKeyboardAwaitingView = false;
		if (video === accessoriesPenVideo)
			accessoriesPenCallout?.classList.remove("is-visible");
		playAccessoriesVideo(video);
	});
};

if (accessoriesKeyboardVideo && "IntersectionObserver" in window) {
	const accessoriesKeyboardObserver = new IntersectionObserver(
		([entry]) => {
			accessoriesKeyboardInView =
				entry.isIntersecting && entry.intersectionRatio >= 0.5;
			if (!accessoriesKeyboardInView || !accessoriesKeyboardAwaitingView)
				return;

			const panel = accessoriesKeyboardVideo.closest(
				"[data-na6c-accessories-panel]",
			);
			if (!panel || panel.hidden) return;

			accessoriesKeyboardAwaitingView = false;
			if (accessoriesKeyboardVideo.readyState)
				accessoriesKeyboardVideo.currentTime = 0;
			playAccessoriesVideo(accessoriesKeyboardVideo);
		},
		{ threshold: 0.5 },
	);
	accessoriesKeyboardObserver.observe(accessoriesKeyboardVideo);
}

accessoriesPenVideo?.addEventListener("ended", () => {
	accessoriesPenCallout?.classList.add("is-visible");
});

const getAccessoriesPanelItems = (panel) =>
	panel
		? [
				...panel.querySelectorAll(
					".na6c-accessories__copy > *, .na6c-accessories__visual",
				),
			]
		: [];

const resetAccessoriesPanelMotion = () => {
	accessoriesPanels.forEach((panel) => {
		gsap.set(getAccessoriesPanelItems(panel), {
			clearProps: "opacity,visibility,transform",
		});
	});
};

const showAccessoriesPanel = (nextPanel) => {
	accessoriesPanels.forEach((panel) => {
		const isActive = panel === nextPanel;
		panel.classList.toggle("is-active", isActive);
		panel.hidden = !isActive;
	});
	syncAccessoriesMedia(nextPanel);
	syncAccessoriesCaseAutoplay();
};

const drawAccessoriesTab = (tab, restart = true) => {
	const draw = tab?.querySelector("[data-na6c-accessories-tab-draw]");
	if (!draw) return;
	if (draw.classList.contains("is-drawn")) return;

	if (restart && !accessoriesReduceMotion) {
		draw.classList.remove("is-drawn");
		void draw.offsetWidth;
	}

	draw.classList.add("is-drawn");
};

const activateAccessoriesTab = (
	nextTab,
	{ focus = false, draw = true } = {},
) => {
	const nextPanelName = nextTab?.dataset.na6cAccessoriesTab;
	const nextPanel = accessoriesPanels.find(
		(panel) => panel.dataset.na6cAccessoriesPanel === nextPanelName,
	);
	if (!nextPanelName || !nextPanel) return;

	accessoriesSection.classList.remove(
		"is-tab-keyboard",
		"is-tab-pen",
		"is-tab-case",
	);
	accessoriesSection.classList.add(`is-tab-${nextPanelName}`);

	accessoriesTabs.forEach((tab) => {
		const isActive = tab === nextTab;
		tab.classList.toggle("is-active", isActive);
		tab.setAttribute("aria-selected", String(isActive));
		tab.tabIndex = isActive ? 0 : -1;
		if (!isActive) {
			tab.querySelector(
				"[data-na6c-accessories-tab-draw]",
			)?.classList.remove("is-drawn");
		}
	});

	if (draw) drawAccessoriesTab(nextTab);
	if (focus) nextTab.focus();

	const wasTransitioning = Boolean(accessoriesPanelTransition);
	if (accessoriesPanelTransition) {
		accessoriesPanelTransition.kill();
		accessoriesPanelTransition = null;
	}
	if (wasTransitioning) resetAccessoriesPanelMotion();

	const currentPanel = accessoriesPanels.find((panel) => !panel.hidden);
	if (!currentPanel || currentPanel === nextPanel) {
		showAccessoriesPanel(nextPanel);
		return;
	}

	if (accessoriesIntroTimeline) {
		accessoriesIntroTimeline.scrollTrigger?.kill();
		accessoriesIntroTimeline.kill();
		accessoriesIntroTimeline = null;
	}
	resetAccessoriesPanelMotion();

	if (accessoriesReduceMotion) {
		showAccessoriesPanel(nextPanel);
		return;
	}

	const currentItems = getAccessoriesPanelItems(currentPanel);
	const nextItems = getAccessoriesPanelItems(nextPanel);

	accessoriesSection.classList.add("is-switching");
	accessoriesPanelTransition = gsap
		.timeline({
			onComplete: () => {
				resetAccessoriesPanelMotion();
				accessoriesSection.classList.remove("is-switching");
				accessoriesPanelTransition = null;
			},
			onInterrupt: () => {
				accessoriesSection.classList.remove("is-switching");
			},
		})
		.to(currentItems, {
			autoAlpha: 0,
			duration: 0.22,
			ease: "power1.in",
			stagger: 0.025,
			yPercent: -2,
		})
		.add(() => {
			gsap.set(currentItems, {
				clearProps: "opacity,visibility,transform",
			});
			showAccessoriesPanel(nextPanel);
		})
		.fromTo(
			nextItems,
			{ autoAlpha: 0, yPercent: 2 },
			{
				autoAlpha: 1,
				duration: 0.42,
				ease: "power2.out",
				stagger: 0.05,
				yPercent: 0,
			},
		);
};

accessoriesTabs.forEach((tab, tabIndex) => {
	tab.addEventListener("click", () => activateAccessoriesTab(tab));
	tab.addEventListener("keydown", (event) => {
		let nextIndex;

		if (event.key === "ArrowRight") {
			nextIndex = (tabIndex + 1) % accessoriesTabs.length;
		}
		if (event.key === "ArrowLeft") {
			nextIndex =
				(tabIndex - 1 + accessoriesTabs.length) %
				accessoriesTabs.length;
		}
		if (event.key === "Home") nextIndex = 0;
		if (event.key === "End") nextIndex = accessoriesTabs.length - 1;
		if (nextIndex === undefined) return;

		event.preventDefault();
		activateAccessoriesTab(accessoriesTabs[nextIndex], { focus: true });
	});
});

const accessoriesCaseSwatches = accessoriesSection
	? [
			...accessoriesSection.querySelectorAll(
				"[data-na6c-accessories-swatch][data-na6c-case-color]",
			),
		]
	: [];
const accessoriesCaseModeButtons = accessoriesSection
	? [...accessoriesSection.querySelectorAll("[data-na6c-case-mode-button]")]
	: [];
const accessoriesCaseVisuals = accessoriesSection
	? [...accessoriesSection.querySelectorAll("[data-na6c-case-visual]")]
	: [];
const accessoriesCaseDots = accessoriesSection
	? [...accessoriesSection.querySelectorAll("[data-na6c-case-dot]")]
	: [];
const accessoriesCasePrevious = accessoriesSection?.querySelector(
	"[data-na6c-case-previous]",
);
const accessoriesCaseNext = accessoriesSection?.querySelector(
	"[data-na6c-case-next]",
);
const accessoriesCasePanel = accessoriesSection?.querySelector(
	"[data-na6c-accessories-panel='case']",
);
const accessoriesCaseGallery = accessoriesSection?.querySelector(
	"[data-na6c-case-gallery]",
);
const accessoriesMobileMedia = window.matchMedia("(max-width: 750px)");
const accessoriesCaseAutoplayDelay = 6000;
let accessoriesCaseAutoplayTimer;
let accessoriesCaseSwipe;

const updateAccessoriesCaseVisual = () => {
	const color = accessoriesCaseSwatches.find(
		(item) => item.getAttribute("aria-pressed") === "true",
	)?.dataset.na6cCaseColor;
	const mode = accessoriesCaseModeButtons.find(
		(item) => item.getAttribute("aria-pressed") === "true",
	)?.dataset.na6cCaseModeButton;

	accessoriesCaseVisuals.forEach((visual) => {
		const isActive =
			visual.dataset.na6cCaseColor === color &&
			visual.dataset.na6cCaseMode === mode;
		visual.classList.toggle("is-active", isActive);
		visual.setAttribute("aria-hidden", String(!isActive));
	});

	const activeIndex = accessoriesCaseVisuals.findIndex((visual) =>
		visual.classList.contains("is-active"),
	);
	accessoriesCaseDots.forEach((dot, index) => {
		if (index === activeIndex) {
			dot.setAttribute("aria-current", "true");
		} else {
			dot.removeAttribute("aria-current");
		}
	});
};

const setAccessoriesCaseVisualByIndex = (index) => {
	if (!accessoriesCaseVisuals.length) return;

	const normalizedIndex =
		(index + accessoriesCaseVisuals.length) % accessoriesCaseVisuals.length;
	const visual = accessoriesCaseVisuals[normalizedIndex];
	const { na6cCaseColor: color, na6cCaseMode: mode } = visual.dataset;

	accessoriesCaseSwatches.forEach((item) => {
		item.setAttribute(
			"aria-pressed",
			String(item.dataset.na6cCaseColor === color),
		);
	});
	accessoriesCaseModeButtons.forEach((item) => {
		item.setAttribute(
			"aria-pressed",
			String(item.dataset.na6cCaseModeButton === mode),
		);
	});
	updateAccessoriesCaseVisual();
};

const stepAccessoriesCaseVisual = (direction) => {
	const currentIndex = Math.max(
		accessoriesCaseVisuals.findIndex((visual) =>
			visual.classList.contains("is-active"),
		),
		0,
	);
	setAccessoriesCaseVisualByIndex(currentIndex + direction);
};

const clearAccessoriesCaseAutoplay = () => {
	if (!accessoriesCaseAutoplayTimer) return;

	window.clearTimeout(accessoriesCaseAutoplayTimer);
	accessoriesCaseAutoplayTimer = null;
};

const syncAccessoriesCaseAutoplay = () => {
	clearAccessoriesCaseAutoplay();
	if (
		!accessoriesMobileMedia.matches ||
		document.hidden ||
		!accessoriesCasePanel ||
		accessoriesCasePanel.hidden
	) {
		return;
	}

	accessoriesCaseAutoplayTimer = window.setTimeout(() => {
		stepAccessoriesCaseVisual(1);
		accessoriesCaseAutoplayTimer = null;
		syncAccessoriesCaseAutoplay();
	}, accessoriesCaseAutoplayDelay);
};

const finishAccessoriesCaseSwipe = (event, allowNavigation = true) => {
	if (!accessoriesCaseSwipe || event.pointerId !== accessoriesCaseSwipe.id)
		return;

	const deltaX = event.clientX - accessoriesCaseSwipe.x;
	const deltaY = event.clientY - accessoriesCaseSwipe.y;
	const swipeThreshold = Math.max(
		36,
		(accessoriesCaseGallery?.clientWidth || 0) * 0.12,
	);

	if (
		allowNavigation &&
		Math.abs(deltaX) >= swipeThreshold &&
		Math.abs(deltaX) > Math.abs(deltaY) * 1.15
	) {
		stepAccessoriesCaseVisual(deltaX < 0 ? 1 : -1);
	}

	if (accessoriesCaseGallery?.hasPointerCapture(event.pointerId)) {
		accessoriesCaseGallery.releasePointerCapture(event.pointerId);
	}
	accessoriesCaseGallery?.classList.remove("is-dragging");
	accessoriesCaseSwipe = null;
	syncAccessoriesCaseAutoplay();
};

accessoriesCaseSwatches.forEach((swatch) => {
	swatch.addEventListener("click", () => {
		accessoriesCaseSwatches.forEach((item) => {
			item.setAttribute("aria-pressed", String(item === swatch));
		});
		updateAccessoriesCaseVisual();
		syncAccessoriesCaseAutoplay();
	});
});

accessoriesCaseModeButtons.forEach((modeButton) => {
	modeButton.addEventListener("click", () => {
		accessoriesCaseModeButtons.forEach((item) => {
			item.setAttribute("aria-pressed", String(item === modeButton));
		});
		updateAccessoriesCaseVisual();
		syncAccessoriesCaseAutoplay();
	});
});

accessoriesCaseDots.forEach((dot) => {
	dot.addEventListener("click", () => {
		setAccessoriesCaseVisualByIndex(Number(dot.dataset.na6cCaseDot));
		syncAccessoriesCaseAutoplay();
	});
});
accessoriesCasePrevious?.addEventListener("click", () => {
	stepAccessoriesCaseVisual(-1);
	syncAccessoriesCaseAutoplay();
});
accessoriesCaseNext?.addEventListener("click", () => {
	stepAccessoriesCaseVisual(1);
	syncAccessoriesCaseAutoplay();
});

accessoriesCaseGallery?.addEventListener("pointerdown", (event) => {
	if (
		accessoriesCasePanel?.hidden ||
		event.button !== 0 ||
		event.target.closest("button")
	) {
		return;
	}

	clearAccessoriesCaseAutoplay();
	accessoriesCaseSwipe = {
		id: event.pointerId,
		x: event.clientX,
		y: event.clientY,
	};
	accessoriesCaseGallery.classList.add("is-dragging");
	accessoriesCaseGallery.setPointerCapture(event.pointerId);
});
accessoriesCaseGallery?.addEventListener("pointermove", (event) => {
	if (!accessoriesCaseSwipe || event.pointerId !== accessoriesCaseSwipe.id)
		return;

	const deltaX = event.clientX - accessoriesCaseSwipe.x;
	const deltaY = event.clientY - accessoriesCaseSwipe.y;
	if (Math.abs(deltaX) > Math.abs(deltaY)) event.preventDefault();
});
accessoriesCaseGallery?.addEventListener("pointerup", (event) => {
	finishAccessoriesCaseSwipe(event);
});
accessoriesCaseGallery?.addEventListener("pointercancel", (event) => {
	finishAccessoriesCaseSwipe(event, false);
});

document.addEventListener("visibilitychange", syncAccessoriesCaseAutoplay);
accessoriesMobileMedia.addEventListener("change", syncAccessoriesCaseAutoplay);

if (accessoriesMobileMedia.matches) {
	setAccessoriesCaseVisualByIndex(2);
} else {
	updateAccessoriesCaseVisual();
}
syncAccessoriesCaseAutoplay();
syncAccessoriesMedia(accessoriesPanels.find((panel) => !panel.hidden));

if (accessoriesReduceMotion) {
	drawAccessoriesTab(
		accessoriesTabs.find((tab) => tab.classList.contains("is-active")),
		false,
	);
}

if (accessoriesSection && !accessoriesReduceMotion) {
	const activeAccessoriesPanel = accessoriesPanels.find(
		(panel) => !panel.hidden,
	);

	accessoriesIntroTimeline = gsap
		.timeline({
			scrollTrigger: {
				trigger: accessoriesSection,
				start: "top 70%",
				once: true,
			},
		})
		.add(() =>
			drawAccessoriesTab(
				accessoriesTabs.find((tab) =>
					tab.classList.contains("is-active"),
				),
			),
		)
		.from(getAccessoriesPanelItems(activeAccessoriesPanel), {
			autoAlpha: 0,
			duration: 0.72,
			ease: "power2.out",
			stagger: 0.06,
			yPercent: 8,
		});
}

const video = document.querySelector(
	".box4-bg:not(.na6c-academic) #box4-video",
);
const container = document.querySelector(
	".box4-bg:not(.na6c-academic) #box4-video-box",
);

if (video && container) {
	video.load();

	const isIPad =
		/iPad/.test(navigator.userAgent) ||
		(navigator.maxTouchPoints > 0 && /Macintosh/.test(navigator.userAgent));
	const isAndroidTab =
		/Android/.test(navigator.userAgent) && window.innerWidth >= 768;
	const isTablet = isIPad || isAndroidTab;
	const isMobile =
		!isTablet && /iPhone|iPod|Android/i.test(navigator.userAgent);

	if (isTablet) {
		ScrollTrigger.create({
			trigger: ".box4",
			start: "top bottom",
			once: true,
			onEnter: () => {
				video.play().catch(() => {});
				container.classList.add("active");
			},
		});
	} else {
		const proxy = { time: 0 };

		let videoUnlocked = !isMobile;
		let unlocking = false;

		const GESTURES = ["touchstart", "pointerdown", "click", "keydown"];

		function unlockVideo() {
			if (videoUnlocked || unlocking) return;
			unlocking = true;

			if (video.readyState === 0) video.load();

			const p = video.play();

			if (p === undefined) {
				finishUnlock();
				return;
			}

			p.then(finishUnlock).catch(() => {
				unlocking = false;
			});
		}

		function finishUnlock() {
			video.pause();
			videoUnlocked = true;
			unlocking = false;

			GESTURES.forEach((evt) =>
				document.removeEventListener(evt, unlockVideo),
			);

			video.currentTime = proxy.time;
		}

		if (isMobile) {
			GESTURES.forEach((evt) =>
				document.addEventListener(evt, unlockVideo, { passive: true }),
			);
		}

		function init() {
			const duration = video.duration;

			gsap.to(proxy, {
				time: duration,
				ease: "none",
				scrollTrigger: {
					trigger: ".box4",
					start: "bottom bottom",
					end: "+=60%",
					pin: true,
					scrub: 0,
					onUpdate: () => {
						if (!videoUnlocked) return;
						if (Math.abs(video.currentTime - proxy.time) > 0.01) {
							video.currentTime = proxy.time;
						}
					},
					onToggle: (self) => {
						container.classList.toggle("active", self.isActive);
					},
				},
			});
		}

		if (video.readyState >= 1) {
			init();
		} else {
			video.addEventListener("loadedmetadata", init, { once: true });
		}
	}
}

const box3 = document.querySelector(".box3:not(.na6c-restful)");
const box3Tab = document.querySelectorAll(".item-switch-but li");

if (box3 && box3Tab.length) {
	const box3Splide = new Splide("#box3-splide", {
		type: "fade",
		perPage: 1,
		arrows: false,
		rewind: true,
		pagination: false,
		drag: false,
	}).mount();

	const box3BgColors = [
		"linear-gradient( #E3E1DB 0%, #F6F4F0 50%, #F6F4F0 100%)",
		"linear-gradient( #D5D6DA 0%, #F6F4F0 50%, #F6F4F0 100%)",
		"linear-gradient( #ACACAE 0%, #F6F4F0 50%, #F6F4F0 100%)",
		"linear-gradient( #E7DFCD 0%, #F6F4F0 50%, #F6F4F0 100%)",
	];

	function switchBox3Tab(index) {
		box3Tab.forEach((item) => item.classList.remove("active"));
		box3Tab[index].classList.add("active");
		box3Splide.go(index);
		if (box3BgColors[index]) {
			box3.style.backgroundImage = box3BgColors[index];
		}
	}

	box3Tab.forEach((item, index) => {
		item.addEventListener("click", () => switchBox3Tab(index));
		item.addEventListener("mouseover", () => switchBox3Tab(index));
	});
}

const box6Tab = document.querySelectorAll(".box6 .right .text-list .text-item");

if (document.getElementById("box6-splide")) {
	const box6Splide = new Splide("#box6-splide", {
		type: "fade",
		perPage: 1,
		gap: 10,
		arrows: true,
		rewind: true,
	}).mount();

	if (box6Tab.length) {
		box6Splide.on("move", () => {
			const activeIndex = box6Splide.index;
			box6Tab.forEach((item) => item.classList.remove("active"));
			box6Tab[activeIndex].classList.add("active");
		});
	}
}

const box8Tab = document.querySelectorAll(".box8-content .tab-click .item");
const box8Text = document.querySelectorAll(".box8-content .tab-text .item");
const box8Img = document.querySelectorAll(".box8 .bg-img>div");
const box8TabHr = document.querySelector(".box8-content .tab-click .hr");

if (box8Tab.length && box8Text.length && box8Img.length && box8TabHr) {
	function switchBox8Tab(index) {
		box8Tab.forEach((item) => item.classList.remove("active"));
		box8Text.forEach((item) => item.classList.remove("active"));
		box8Img.forEach((item) => item.classList.remove("active"));
		box8Text[index].classList.add("active");
		box8Img[index].classList.add("active");
		box8Tab[index].classList.add("active");
		box8TabHr.style.transform = `translateX(${index * 100}%)`;
	}

	box8Tab.forEach((item, index) => {
		item.addEventListener("click", () => switchBox8Tab(index));
		item.addEventListener("mouseover", () => switchBox8Tab(index));
	});
}

if (
	document.getElementById("box9-splide") &&
	!document.querySelector(".na6c-study-hub")
) {
	new Splide("#box9-splide", {
		type: "loop",
		perPage: 1.5,
		focus: "center",
		lazyLoad: "nearby",
		updateOnMove: true,
		arrows: true,
		breakpoints: {
			1024: {
				perPage: 1.5,
			},
			750: {
				perPage: 1,
			},
		},
	}).mount();
}

const restoreNa6cInitialScrollAnchor = () => {
	if (!na6cInitialScrollAnchor?.element?.isConnected) return;

	const anchorTop =
		na6cInitialScrollAnchor.element.getBoundingClientRect().top;
	const layoutShift = anchorTop - na6cInitialScrollAnchor.top;

	if (Math.abs(layoutShift) > 1) {
		window.scrollBy({ behavior: "auto", left: 0, top: layoutShift });
	}
	ScrollTrigger.update();
};

restoreNa6cInitialScrollAnchor();

const refreshNa6cAfterLoad = () => {
	if (window.scrollY <= window.innerHeight * 0.25) {
		ScrollTrigger.refresh();
	} else {
		// Triggers were already measured when they were created. Updating their
		// state is enough here and does not tear down the long pin spacers.
		ScrollTrigger.update();
	}
};

if (document.readyState === "complete") {
	refreshNa6cAfterLoad();
} else {
	window.addEventListener("load", refreshNa6cAfterLoad, { once: true });
}

const getNa6cViewportMode = () => {
	if (window.innerWidth <= 750) return "mobile";
	if (window.innerWidth <= 1024) return "tablet";
	return "desktop";
};

let na6cViewportMode = getNa6cViewportMode();
let na6cResizeTimer;

window.addEventListener(
	"resize",
	() => {
		clearTimeout(na6cResizeTimer);

		na6cResizeTimer = setTimeout(() => {
			const nextMode = getNa6cViewportMode();

			if (nextMode !== na6cViewportMode) {
				window.location.reload();
			}
		}, 16);
	},
	{ passive: true },
);
